import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { TelegramEmbeds, RichText, extractTelegramLinks } from '../components/TelegramEmbed'
import { operatorsApi, auditApi, categoriesApi, templatesApi, settingsApi, backupsApi, emojisApi, resolveAvatarUrl, BASE } from '../api'
import { Card, Modal, OpStatusBadge, ProgressBar, AuditRow, SettingsRow, Toggle, Pagination, ConfirmDeleteModal } from '../components/UI'
import {
  Alert, Avatar, Badge, Beacon, Button, CardActions, ChipSelect, ConfirmModal, Coverage, EmojiTextarea,
  EmptyState as KitEmptyState, Field, Freshness, Input, Loader, PageHeader, SearchInput,
  Segmented, Select, Skeleton, SortMenu, TgText, Toolbar,
  stripTgEmoji, stripTgHtml, validateTgHtml,
} from '../ui'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { Spinner, ErrorBox, EmptyState, SkeletonRow } from '../hooks/useApi.jsx'
import { useAuthStore, useStore } from '../store'
import AIOperatorCard from '../components/AIOperatorCard'
import VTSettingsCard from '../components/VTSettingsCard'
import R2SettingsCard from '../components/R2SettingsCard'
import { Bots } from './BotsAndProfile'
import { UserBots } from './UserBots'
import {
  HiCheck, HiXMark, HiArrowPath, HiPencil, HiTrash, HiKey, HiShieldCheck, HiSparkles, HiPhoto, HiArrowDownTray, HiArrowPathRoundedSquare,
  HiPaperAirplane, HiTag, HiClipboardDocumentList, HiClock, HiBell, HiLockClosed, HiCreditCard, HiCircleStack,
  HiCheckCircle, HiVideoCamera, HiDocumentText, HiArrowsUpDown, HiArrowTopRightOnSquare, HiPause,
  HiPlus, HiExclamationTriangle
} from 'react-icons/hi2'

// ═══════════════════════════════════════════════════════
//  OPERATORS
// ═══════════════════════════════════════════════════════
/**
 * Operator avatari — kitning `<Avatar>` iga yupqa o'ram.
 * Rasm bo'lmasa kit neytral yuzada `var(--ui-text-3)` siluet chizadi,
 * ya'ni tungi mavzuda ham ko'rinadi (avval oq siluet oq fonda yo'qolardi).
 */
function OperatorAvatar({ name = '', size = 44, url = null }) {
  // `style` berilmaydi: kit Avatar `...rest` ni `style={box}` dan keyin yoyadi,
  // ya'ni tashqi `style` o'lchamni bekor qilardi. Siqilmaslikni `.ui-avatar { flex: none }` beradi.
  return <Avatar name={name} src={resolveAvatarUrl(url) || undefined} size={size} />
}

export function Operators() {
  const { isSuperAdmin } = useAuthStore()
  const { addToast } = useStore()
  const [operators, setOps] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modal, setModal] = useState(false)
  const [editOp, setEditOp] = useState(null)
  const [deleteOp, setDeleteOp] = useState(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')
  const [form, setForm] = useState({ username: '', email: '', password: '', full_name: '', phone: '', department: '', max_tickets: 20 })
  const [passForm, setPassForm] = useState({ new_password: '' })
  const [passModal, setPassModal] = useState(null)
  // task 1 — ruxsatlar
  const [availablePerms, setAvailablePerms] = useState([])
  const [permModal, setPermModal] = useState(null)   // tahrirlanayotgan operator
  const [permDraft, setPermDraft] = useState([])
  const [permSaving, setPermSaving] = useState(false)
  // AI operator qo'shish
  const [aiModal, setAiModal] = useState(false)
  const [aiName, setAiName] = useState('')
  const [aiFile, setAiFile] = useState(null)
  const [aiSaving, setAiSaving] = useState(false)

  const saveAiOp = async () => {
    if (!aiName.trim()) return
    setAiSaving(true)
    try {
      const created = await operatorsApi.createAi(aiName.trim())
      if (aiFile) { try { await operatorsApi.uploadAvatar(created.id, aiFile) } catch {} }
      setAiModal(false); setAiName(''); setAiFile(null)
      await load()
    } catch (e) { addToast(e.message, 'critical') }
    finally { setAiSaving(false) }
  }

  const load = useCallback(async (silent = false) => {
    if (!silent) { setLoading(true); setError(null) }
    try { setOps(await operatorsApi.list()) }
    catch (e) { if (!silent) setError(e.message) }
    finally { if (!silent) setLoading(false) }
  }, [])

  useEffect(() => { load(); operatorsApi.availablePermissions().then(setAvailablePerms).catch(() => {}) }, [load])

  // task 5 — operator holatini (onlayn/oflayn) jimgina yangilab turamiz
  useEffect(() => {
    const id = setInterval(() => { load(true) }, 10000)
    return () => clearInterval(id)
  }, [load])

  const openPerms = (op) => { setPermModal(op); setPermDraft(Array.isArray(op.permissions) ? [...op.permissions] : []) }
  const togglePerm = (key) => setPermDraft(d => d.includes(key) ? d.filter(x => x !== key) : [...d, key])
  const handleSavePerms = async () => {
    setPermSaving(true)
    try { await operatorsApi.updatePermissions(permModal.id, permDraft); setPermModal(null); await load() }
    catch (e) { addToast(e.message, 'critical') }
    finally { setPermSaving(false) }
  }

  const openAdd = () => {
    setEditOp(null)
    setForm({ username: '', email: '', password: '', full_name: '', phone: '', department: '', max_tickets: 20 })
    setFormError(''); setModal(true)
  }
  const openEdit = (op) => {
    setEditOp(op)
    setForm({ username: op.username, email: op.email, password: '', full_name: op.full_name, phone: op.phone || '', department: op.department || '', max_tickets: op.max_tickets })
    setFormError(''); setModal(true)
  }

  const handleSave = async () => {
    setFormError('')
    if (!form.full_name.trim()) return setFormError("Ism Familiya kiritilishi shart")
    if (!form.email.trim()) return setFormError("Email kiritilishi shart")
    if (!editOp) {
      if (!form.username.trim()) return setFormError("Username kiritilishi shart")
      if (!form.password) return setFormError("Parol kiritilishi shart")
      if (form.password.length < 12) return setFormError("Parol kamida 12 ta belgi bo'lishi kerak")
      if (!/[A-Z]/.test(form.password)) return setFormError("Parolda kamida bitta KATTA harf bo'lishi kerak (masalan: A)")
      if (!/[0-9]/.test(form.password)) return setFormError("Parolda kamida bitta raqam bo'lishi kerak (masalan: 1)")
    }
    setSaving(true)
    try {
      if (editOp) {
        await operatorsApi.update(editOp.id, {
          full_name: form.full_name, email: form.email,
          phone: form.phone || null, department: form.department || null,
          max_tickets: form.max_tickets,
        })
      } else {
        await operatorsApi.create({
          username: form.username.toLowerCase().trim(),
          email: form.email.trim(),
          password: form.password,
          full_name: form.full_name.trim(),
          phone: form.phone || null,
          department: form.department || null,
          max_tickets: form.max_tickets,
          role: 'operator',
        })
      }
      setModal(false); await load()
    } catch (e) { setFormError(e.message) }
    finally { setSaving(false) }
  }
  const handleDelete = (op) => {
    setDeleteOp(op)
  }

  const confirmDelete = async () => {
    if (!deleteOp) return
    try { await operatorsApi.delete(deleteOp.id); await load(); setDeleteOp(null) } catch (e) { addToast(e.message, 'critical') }
  }

  const handleResetPass = async () => {
    if (!passForm.new_password || passForm.new_password.length < 12) return addToast("Kamida 12 belgi", 'critical')
    try { await operatorsApi.resetPassword(passModal.id, passForm.new_password); setPassModal(null); addToast("Parol yangilandi", 'message') }
    catch (e) { addToast(e.message, 'critical') }
  }

  return (
    <div className="page-enter">
      <div className="page-hdr">
        <div className="page-hdr-left"><h2>Operatorlar</h2><p>{operators.length} ta operator</p></div>
        <div className="page-hdr-right">
          <button className="btn-refresh-icon" onClick={load} title="Yangilash" aria-label="Operatorlar ro'yxatini yangilash">
            <HiArrowPath />
          </button>
          {operators.length > 0 && <button className="btn btn-primary" onClick={openAdd}>+ Yangi operator</button>}
        </div>
      </div>

      <ErrorBox error={error} onRetry={load} />



      {/* AI operator profillari — ismli, avatarli, navbat bilan javob beradi */}
      {isSuperAdmin() && (() => {
        const aiOps = operators.filter(o => o.is_ai)
        return (
          <Card title="AI operatorlar" icon={HiSparkles} style={{ marginBottom: 18 }}
            actions={<button className="btn btn-primary btn-sm" onClick={() => { setAiName(''); setAiFile(null); setAiModal(true) }}>+ AI operator qo'shish</button>}>
            <div className="card-body">
              {aiOps.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--ui-text-3)', padding: '6px 0' }}>
                  Hozircha AI operator yo'q. «+ AI operator qo'shish» orqali ism va avatar bilan qo'shing —
                  ular murojaatlarga navbat bilan javob beradi (huddi tirik operatordek panelда ko'rinadi).
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 12 }}>
                  {aiOps.map(op => (
                    <div key={op.id} style={{ border: '1px solid var(--ui-border)', borderRadius: 'var(--ui-r-lg)', padding: 12, display: 'flex', alignItems: 'center', gap: 10, background: 'var(--ui-surface)' }}>
                      <div style={{ position: 'relative' }}>
                        <OperatorAvatar name={op.full_name} url={op.avatar_url} size={44} />
                        <span style={{ position: 'absolute', bottom: -2, right: -2, background: 'var(--ui-text-1)', color: 'var(--ui-surface)', fontSize: 'var(--ui-fs-2xs)', fontWeight: 600, lineHeight: 1, padding: '2px 4px', borderRadius: 'var(--ui-r)', border: '2px solid var(--ui-surface)' }}>AI</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{op.full_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--ui-text-3)' }}>{op.ai_reply_count || 0} ta javob berdi</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label className="btn btn-secondary btn-sm btn-icon" title="Avatar yuklash" style={{ cursor: 'pointer' }}>
                          <HiPhoto size={14} aria-hidden="true" />
                          <span className="ui-sr-only">Avatar yuklash</span>
                          <input type="file" accept="image/*" hidden onChange={async e => { const f = e.target.files?.[0]; if (f) { try { await operatorsApi.uploadAvatar(op.id, f); await load() } catch (er) { addToast(er.message, 'critical') } } }} />
                        </label>
                        <button className="btn btn-danger btn-sm btn-icon" title="O'chirish" aria-label={`${op.full_name} — AI operatorni o'chirish`} onClick={() => handleDelete(op)}><HiTrash size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        )
      })()}

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
          <Loader state="working" size="lg" block />
        </div>
      ) : operators.filter(o => !o.is_ai).length === 0 ? (
        <EmptyState icon="" text="Operator yo'q" action={<button className="btn btn-primary" onClick={openAdd}>+ Qo'shish</button>} />
      ) : (
        <>
          <div className="mb-20" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 16 }}>
            {operators.filter(o => !o.is_ai).map(op => (
              <div key={op.id} className="op-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <OperatorAvatar name={op.full_name} url={op.avatar_url} size={48} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{op.full_name}</div>
                    <OpStatusBadge status={op.is_online ? 'online' : 'offline'} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
                  {[{l:'Faol',v:op.total_tickets - op.closed_tickets,c:'var(--ui-text-1)'},{l:'Yopdi',v:op.closed_tickets,c:'var(--ui-st-ok)'},{l:'Reyting',v:op.satisfaction_avg.toFixed(1),c:'var(--ui-st-medium)'}].map(s => (
                    <div key={s.l} style={{ textAlign: 'center', background: 'var(--ui-surface-2)', borderRadius: 'var(--ui-r)', padding: '8px 4px' }}>
                      <div style={{ fontSize: 18, fontWeight: 600, color: s.c, fontVariantNumeric: 'tabular-nums' }}>{s.v}</div>
                      <div style={{ fontSize: 11, color: 'var(--ui-text-3)', marginTop: 4, fontWeight: 500 }}>{s.l}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <ProgressBar value={op.total_tickets ? Math.round(op.closed_tickets / op.total_tickets * 100) : 0} />
                </div>
                {/* Ruxsatlar (task 1) */}
                <div style={{ marginBottom: 12, minHeight: 22 }}>
                  {(op.permissions && op.permissions.length > 0) ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {op.permissions.map(pk => {
                        const meta = availablePerms.find(a => a.key === pk)
                        return <span key={pk} style={{ fontSize: 11, fontWeight: 500, padding: '2px 7px', borderRadius: 'var(--ui-r)', background: 'var(--ui-surface-2)', color: 'var(--ui-text-1)' }}>{meta?.uz || pk}</span>
                      })}
                    </div>
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--ui-text-3)' }}>Qo'shimcha ruxsatlar yo'q</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary" style={{ flex: 1, justifyContent: 'center', fontSize: 12 }} onClick={() => openEdit(op)}>Tahrirlash</button>
                  <button className="btn btn-secondary btn-sm btn-icon" title="Ruxsatlar" aria-label={`${op.full_name} — ruxsatlar`} onClick={() => openPerms(op)}><HiShieldCheck /></button>
                  <button className="btn btn-secondary btn-sm btn-icon" title="Parol o'zgartirish" aria-label={`${op.full_name} — parolni o'zgartirish`} onClick={() => { setPassModal(op); setPassForm({ new_password: '' }) }}><HiKey /></button>
                  <button className="btn btn-danger btn-sm btn-icon" title="O'chirish" aria-label={`${op.full_name} — o'chirish`} onClick={() => handleDelete(op)}><HiTrash /></button>
                </div>
              </div>
            ))}
          </div>

          <Card title="Samaradorlik jadval">
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>Operator</th><th>Status</th><th>Jami</th><th>Yopdi</th><th>Reyting</th><th>Amallar</th></tr></thead>
                <tbody>
                  {operators.filter(o => !o.is_ai).map(op => (
                    <tr key={op.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <OperatorAvatar name={op.full_name} url={op.avatar_url} size={34} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>{op.full_name}</div>
                            <div style={{ fontSize: 11, color: 'var(--ui-text-3)' }}>@{op.username}</div>
                          </div>
                        </div>
                      </td>
                      <td><OpStatusBadge status={op.is_online ? 'online' : 'offline'} /></td>
                      <td style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{op.total_tickets}</td>
                      <td style={{ color: 'var(--ui-st-ok)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{op.closed_tickets}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <ProgressBar value={(op.satisfaction_avg / 5) * 100} tone="ok" />
                          <span style={{ fontSize: 12, fontWeight: 600, minWidth: 28, fontVariantNumeric: 'tabular-nums' }}>{op.satisfaction_avg.toFixed(1)}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-secondary btn-sm btn-icon" title="Tahrirlash" aria-label={`${op.full_name} — tahrirlash`} onClick={() => openEdit(op)}><HiPencil /></button>
                          <button className="btn btn-danger btn-sm btn-icon" title="O'chirish" aria-label={`${op.full_name} — o'chirish`} onClick={() => handleDelete(op)}><HiTrash /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Add/Edit Modal */}
      <Modal open={modal} onClose={() => setModal(false)} title={editOp ? 'Operatorni tahrirlash' : "Yangi operator qo'shish"}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModal(false)}>Bekor</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? <Spinner size={14} /> : <><HiCheck /> Saqlash</>}</button>
        </>}>
        {formError && <Alert tone="danger" style={{ marginBottom: 16 }}>{formError}</Alert>}
        <div className="g2">
          <div className="form-group"><label className="form-label">Ism Familiya *</label><input className="form-input" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} placeholder="Alisher Nazarov" /></div>
          <div className="form-group"><label className="form-label">Username {editOp ? '(o`zgarmaydi)' : '*'}</label><input className="form-input" value={form.username} onChange={e => setForm({...form, username: e.target.value})} placeholder="a.nazarov" disabled={!!editOp} style={editOp ? {opacity:.6}:{}} /></div>
        </div>
        <div className="form-group"><label className="form-label">Email *</label><input className="form-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="a.nazarov@jkxm.uz" /></div>
        <div className="g2">
          <div className="form-group"><label className="form-label">Telefon</label><input className="form-input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+998901234567" /></div>
          <div className="form-group"><label className="form-label">Bo'lim</label><input className="form-input" value={form.department} onChange={e => setForm({...form, department: e.target.value})} /></div>
        </div>
        {!editOp && (
          <div className="form-group">
            <label className="form-label">Parol *</label>
            <input className="form-input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Kamida 12 belgi" />
            <div style={{fontSize:11,color:'var(--ui-text-3)',marginTop:4,lineHeight:1.7}}>
              Kamida 12 belgi &nbsp;|&nbsp; Bitta KATTA harf (A-Z) &nbsp;|&nbsp; Bitta raqam (0-9)<br/>
              <span style={{color:'var(--ui-text-1)',fontWeight:600}}>Misol: Admin@123456</span>
            </div>
          </div>
        )}
        <div className="form-group"><label className="form-label">Max ticket: {form.max_tickets} ta</label><input className="form-input" type="range" min={1} max={50} value={form.max_tickets} onChange={e => setForm({...form, max_tickets: +e.target.value})} /></div>
      </Modal>

      {/* AI operator qo'shish modal */}
      <Modal open={aiModal} onClose={() => setAiModal(false)} title="AI operator qo'shish"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setAiModal(false)}>Bekor</button>
          <button className="btn btn-primary" onClick={saveAiOp} disabled={aiSaving || !aiName.trim()}>{aiSaving ? <Spinner size={14} /> : <><HiCheck /> Qo'shish</>}</button>
        </>}>
        <div style={{ fontSize: 12, color: 'var(--ui-text-3)', marginBottom: 16, lineHeight: 1.6 }}>
          AI operator ism va avatar bilan panelда tirik operatordek ko'rinadi. Suhbatlar unga navbat bilan
          biriktiriladi — javobni sun'iy intellekt beradi.
        </div>
        <div className="form-group">
          <label className="form-label">Ism *</label>
          <input className="form-input" value={aiName} onChange={e => setAiName(e.target.value)} placeholder="Masalan: Dilshod Operator" autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Avatar (ixtiyoriy)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <OperatorAvatar name={aiName || '?'} url={aiFile ? URL.createObjectURL(aiFile) : null} size={48} />
            <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
              <HiPhoto size={14} /> Rasm tanlash
              <input type="file" accept="image/*" hidden onChange={e => setAiFile(e.target.files?.[0] || null)} />
            </label>
            {aiFile && <span style={{ fontSize: 12, color: 'var(--ui-text-3)' }}>{aiFile.name}</span>}
          </div>
        </div>
      </Modal>
      <Modal open={!!passModal} onClose={() => setPassModal(null)} title={`Parolni yangilash: ${passModal?.full_name}`}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setPassModal(null)}>Bekor</button>
          <button className="btn btn-primary" onClick={handleResetPass}>Yangilash</button>
        </>}>
        <div className="form-group">
          <label className="form-label">Yangi parol (min 12 belgi)</label>
          <input className="form-input" type="password" value={passForm.new_password} onChange={e => setPassForm({new_password: e.target.value})} placeholder="••••••••••••" autoFocus />
        </div>
      </Modal>

      {/* Ruxsatlar modal (task 1) */}
      <Modal open={!!permModal} onClose={() => setPermModal(null)} title={`Ruxsatlar: ${permModal?.full_name || ''}`}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setPermModal(null)}>Bekor</button>
          <button className="btn btn-primary" onClick={handleSavePerms} disabled={permSaving}>{permSaving ? <Spinner size={14} /> : <><HiCheck /> Saqlash</>}</button>
        </>}>
        <p style={{ fontSize: 13, color: 'var(--ui-text-3)', marginBottom: 16 }}>
          Operatorga qaysi bo'limlarni boshqarish huquqini berasiz. (Super admin hamma narsaga ega.)
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {availablePerms.map(p => {
            const on = permDraft.includes(p.key)
            return (
              <button type="button" key={p.key} onClick={() => togglePerm(p.key)} aria-pressed={on}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px', borderRadius: 'var(--ui-r)', cursor: 'pointer',
                  border: `1px solid ${on ? 'var(--ui-text-1)' : 'var(--ui-border)'}`,
                  background: on ? 'var(--ui-surface-2)' : 'var(--ui-surface)' }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: on ? 'var(--ui-text-1)' : 'var(--ui-text-2)' }}>{p.uz}</span>
                <span aria-hidden="true" style={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: on ? 'var(--ui-text-1)' : 'transparent', border: on ? 'none' : '1px solid var(--ui-border)',
                  color: 'var(--ui-surface)', fontSize: 12 }}>
                  {on && <HiCheck size={12} />}
                </span>
              </button>
            )
          })}
        </div>
      </Modal>

      <ConfirmDeleteModal
        open={!!deleteOp}
        onClose={() => setDeleteOp(null)}
        onConfirm={confirmDelete}
        itemName={deleteOp?.full_name || ''}
      />
    </div>
  )
}

// ═══════════════════════════════════════════════════════
//  REPORTS
// ═══════════════════════════════════════════════════════
export function Reports() {
  const [reportType, setReportType] = useState('monthly')
  const [dateFrom, setDateFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0])
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0])
  const [format, setFormat] = useState('pdf')
  const [opData, setOpData] = useState([])

  useEffect(() => {
    fetch(`${BASE}/dashboard/operators_stats`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
    }).then(r => r.json()).then(setOpData).catch(() => {})
  }, [])

  // Real KPI — dashboard/stats dan olish
  const [kpiStats, setKpiStats] = useState(null)
  useEffect(() => {
    fetch(`${BASE}/dashboard/stats`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
    }).then(r => r.json()).then(setKpiStats).catch(() => {})
  }, [])

  // CSV yuklab olish
  const handleDownload = async () => {
    try {
      const params = new URLSearchParams({ from: dateFrom, to: dateTo, format })
      const res = await fetch(`${BASE}/reports/export?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      })
      if (!res.ok) throw new Error()
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `hisobot_${dateFrom}_${dateTo}.${format === 'excel' ? 'xlsx' : format}`
      a.click(); URL.revokeObjectURL(url)
    } catch {
      // Fallback: CSV sifatida yuklab olish
      const rows = [['Murojaat №','Holat','Muhimlik','Sana']]
      const csvContent = rows.map(r => r.join(',')).join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = `hisobot_${dateFrom}.csv`
      a.click()
    }
  }

  const total = kpiStats ? (kpiStats.open_tickets || 0) + (kpiStats.closed_tickets || 0) : 0
  const resolve_pct = kpiStats && total > 0
    ? Math.round((kpiStats.closed_tickets / total) * 100) : 0
  const frt = kpiStats?.avg_frt_hours > 0 ? `${kpiStats.avg_frt_hours} soat` : '—'
  const sat = kpiStats?.satisfaction_avg > 0 ? `${kpiStats.satisfaction_avg} / 5` : '—'

  return (
    <div className="page-enter">
      <div className="page-hdr">
        <div className="page-hdr-left"><h2>Hisobotlar va Tahlil</h2><p>Statistika, KPI va eksport</p></div>
      </div>
      <div className="g4 mb-20">
        {[
          { label: 'Mamnuniyat',         val: sat,               color: 'var(--ui-text-1)',    tone: undefined, pct: 80 },
          { label: 'Birinchi javob',     val: frt,               color: 'var(--ui-st-medium)', tone: 'medium', pct: 70 },
          { label: 'Hal etish darajasi', val: `${resolve_pct}%`, color: 'var(--ui-st-ok)',     tone: 'ok',     pct: resolve_pct },
        ].map(k => (
          <div key={k.label} className="card" style={{ padding: 24, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 600, color: k.color, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>{k.val}</div>
            <div style={{ fontSize: 12, color: 'var(--ui-text-2)', fontWeight: 500, marginTop: 4 }}>{k.label}</div>
            <div style={{ marginTop: 12 }}><ProgressBar value={k.pct} tone={k.tone} /></div>
          </div>
        ))}
      </div>
      <div className="g2">
        <Card title="Hisobot yaratish">
          <div className="card-body">
            <div className="form-group"><label className="form-label">Hisobot turi</label>
              <select className="form-select" value={reportType} onChange={e => setReportType(e.target.value)}>
                <option value="daily">Kunlik</option><option value="weekly">Haftalik</option>
                <option value="monthly">Oylik</option><option value="custom">Maxsus muddat</option>
              </select>
            </div>
            <div className="g2">
              <div className="form-group"><label className="form-label">Boshlanish</label><input className="form-input" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Tugash</label><input className="form-input" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
            </div>
            <div className="form-group"><label className="form-label">Format</label>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                {['pdf','excel','csv'].map(f => (
                  <label key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: `1px solid ${format===f ? 'var(--ui-text-1)' : 'var(--ui-border)'}`, borderRadius: 'var(--ui-r)', cursor: 'pointer', fontSize: 13, fontWeight: format===f ? 600 : 500, background: format===f ? 'var(--ui-surface-2)' : 'var(--ui-surface)', color: format===f ? 'var(--ui-text-1)' : 'var(--ui-text-2)' }}>
                    <input type="radio" name="fmt" checked={format===f} onChange={() => setFormat(f)} style={{ accentColor: 'var(--ui-text-1)' }} />
                    {f === 'pdf' ? 'PDF' : f === 'excel' ? 'Excel' : 'CSV'}
                  </label>
                ))}
              </div>
            </div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}
              onClick={handleDownload}>Yuklab olish</button>
          </div>
        </Card>
        {opData.length > 0 && (
          <Card title="Operator samaradorligi">
            <div className="card-body">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={opData.map(o => ({ name: o.name.split(' ')[0], Yopildi: o.closed, Faol: o.active }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--ui-border)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--ui-text-3)' }} />
                  <YAxis tick={{ fontSize: 11, fill: 'var(--ui-text-3)' }} />
                  <Tooltip contentStyle={{ background: 'var(--ui-surface)', border: '1px solid var(--ui-border)', borderRadius: 'var(--ui-r)', fontSize: 12, color: 'var(--ui-text-1)' }} />
                  <Bar dataKey="Yopildi" fill="var(--ui-text-1)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Faol" fill="var(--ui-text-3)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════
//  AUDIT
// ═══════════════════════════════════════════════════════
export function Audit() {
  const [logs, setLogs]   = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage]   = useState(1)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const res = await auditApi.list({ skip: (page - 1) * 20, limit: 20 })
      setLogs(res?.items || []); setTotal(res?.total || 0)
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }, [page])

  useEffect(() => { load() }, [load])

  // Amal turiga qarab ikonka — emoji o'rniga hi2 (DESIGN.md 8-bo'lim).
  const ACTION_ICON = {
    login: HiKey, logout: HiLockClosed,
    ticket_create: HiClipboardDocumentList, ticket_update: HiPencil,
    ticket_assign: HiPaperAirplane, ticket_close: HiCheckCircle,
    operator_create: HiShieldCheck, operator_update: HiPencil, operator_delete: HiTrash,
    citizen_create: HiPlus, citizen_update: HiPencil, citizen_delete: HiTrash,
    bot_add: HiPaperAirplane, settings_update: HiTag, file_upload: HiPhoto,
  }
  // Yuza rangi — faqat tokenlar. Monoxrom tizimda amal turi rang tashimaydi,
  // shuning uchun neytral yuza; status ma'nosi borlarigina --ui-st-*-bg oladi.
  const ACTION_BG = {
    ticket_create: 'var(--ui-st-ok-bg)', ticket_close: 'var(--ui-st-ok-bg)',
    ticket_assign: 'var(--ui-st-ok-bg)', ticket_update: 'var(--ui-st-medium-bg)',
    operator_delete: 'var(--ui-st-critical-bg)',
    citizen_delete: 'var(--ui-st-critical-bg)',
  }

  return (
    <Card
      title="Audit Jurnali"
      actions={
        <button className="btn-refresh-icon" onClick={load} title="Yangilash" aria-label="Audit jurnalini yangilash">
          <HiArrowPath />
        </button>
      }
    >
      <div className="card-body" style={{ padding: 0 }}>
        <ErrorBox error={error} onRetry={load} />
        <div style={{ padding: '0 20px' }}>
          {loading
            ? <div style={{ padding: 20, textAlign: 'center' }}><Spinner /></div>
            : logs.length === 0
              ? <EmptyState icon="" text="Audit yozuvlari yo'q" />
              : logs.map((l, i) => (
                <AuditRow key={i}
                  ic={ACTION_ICON[l.action] || null}
                  bg={ACTION_BG[l.action] || 'var(--ui-surface-2)'}
                  user={l.user_name || 'Tizim'}
                  action={l.details || l.action}
                  obj={l.resource_id || '—'}
                  ip={l.ip_address || '—'}
                  time={new Date(l.created_at).toLocaleString('uz-UZ')}
                />
              ))
          }
        </div>
        <Pagination total={total} page={page} perPage={20} onChange={setPage} />
      </div>
    </Card>
  )
}

// ═══════════════════════════════════════════════════════
//  CATEGORIES
// ═══════════════════════════════════════════════════════
export function Categories() {
  const { addToast } = useStore()
  const [cats, setCats] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modal, setModal] = useState(false)
  const [editCat, setEditCat] = useState(null)
  const [form, setForm] = useState({ name_uz: '', name_ru: '', icon: '', sort_order: 0 })
  const [saving, setSaving] = useState(false)
  const [delCat, setDelCat] = useState(null)   // o'chirish tasdig'i kutayotgan kategoriya

  // Boshqaruv sahifasi O'CHIRILGANLARINI HAM ko'radi: o'chirish soft va
  // kaskadsiz, ya'ni turkum ichidagi ticket/shablon/resurslar joyida qoladi va
  // AI ularni ishlatishda davom etadi. Ilgari bunday turkum ro'yxatdan
  // butunlay yo'qolar va uni qaytarishning yo'li yo'q edi.
  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try { setCats(await categoriesApi.list(true)) }
    catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const openAdd = () => { setEditCat(null); setForm({ name_uz: '', name_ru: '', icon: '', sort_order: cats.length + 1 }); setModal(true) }
  const openEdit = c => { setEditCat(c); setForm({ name_uz: c.name_uz, name_ru: c.name_ru, icon: c.icon, sort_order: c.sort_order }); setModal(true) }

  const handleSave = async () => {
    if (!form.name_uz) return addToast("O'zbekcha nomi kerak", 'critical')
    setSaving(true)
    try {
      if (editCat) await categoriesApi.update(editCat.id, form)
      else await categoriesApi.create(form)
      setModal(false); await load()
    } catch (e) { addToast(e.message, 'critical') }
    finally { setSaving(false) }
  }

  const confirmDelete = async () => {
    if (!delCat) return
    try {
      const res = await categoriesApi.delete(delCat.id)
      // Server nima "ichkarida qolganini" qaytaradi — admin buni bilib tursin,
      // aks holda turkum jimgina yo'qolib, ichidagi ticketlar osilib qoladi.
      const o = res?.orphaned
      if (o && (o.tickets || o.templates || o.faqs)) {
        addToast(`Turkum o'chirildi, lekin ichida ${o.tickets} murojaat, ` +
                 `${o.templates} shablon va ${o.faqs} resurs qoldi — ularni ` +
                 `boshqa turkumga o'tkazing yoki turkumni qaytaring.`, 'high')
      }
      await load(); setDelCat(null)
    } catch (e) { addToast(e.message, 'critical') }
  }

  const restoreCat = async (c) => {
    try { await categoriesApi.update(c.id, { is_active: true }); await load() }
    catch (e) { addToast(e.message, 'critical') }
  }

  return (
    <Card
      title="Kategoriyalar"
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-refresh-icon" onClick={load} title="Yangilash" aria-label="Kategoriyalarni yangilash">
            <HiArrowPath />
          </button>
          <button className="btn btn-primary" style={{ padding: '6px 12px', fontSize: 13 }} onClick={openAdd}>+ Qo'shish</button>
        </div>
      }
    >
      <div className="card-body" style={{ padding: 0 }}>
        <ErrorBox error={error} onRetry={load} />
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Icon</th><th>Nomi (UZ)</th><th>Nomi (RU)</th><th>Faol</th><th>Tartib</th><th>Amallar</th></tr></thead>
            <tbody>
              {loading ? Array(5).fill(0).map((_, i) => <SkeletonRow key={i} cols={6} />) :
               cats.length === 0 ? <tr><td colSpan={6}><EmptyState text="Kategoriya yo'q" subtext="Hozircha hech qanday murojaat kategoriyasi yaratilmagan." action={<button className="btn btn-primary" onClick={openAdd}>+ Qo'shish</button>} /></td></tr> :
               cats.map(c => (
                <tr key={c.id}>
                  <td style={{ fontSize: 18 }}>{c.icon}</td>
                  <td style={{ fontWeight: 600 }}>{c.name_uz}</td>
                  <td style={{ color: 'var(--ui-text-3)' }}>{c.name_ru}</td>
                  <td><span className={`badge badge-${c.is_active !== false ? 'resolved' : 'closed'}`}>{c.is_active !== false ? <><HiCheck /> Faol</> : <><HiXMark /> Nofaol</>}</span></td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>{c.sort_order}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm btn-icon" title="Tahrirlash" aria-label={`${c.name_uz} — tahrirlash`} onClick={() => openEdit(c)}><HiPencil /></button>
                      {c.is_active === false
                        ? <button className="btn btn-success btn-sm" style={{ gap: 6 }} title="Qaytarish"
                                  aria-label={`${c.name_uz} — qaytarish`} onClick={() => restoreCat(c)}><HiCheck /> Qaytarish</button>
                        : <button className="btn btn-danger btn-sm btn-icon" title="O'chirish" aria-label={`${c.name_uz} — o'chirish`} onClick={() => setDelCat(c)}><HiTrash /></button>}
                    </div>
                  </td>
                </tr>
               ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editCat ? 'Kategoriyani tahrirlash' : "Yangi kategoriya"}
        footer={<><button className="btn btn-secondary" onClick={() => setModal(false)}>Bekor</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? <Spinner size={14}/> : ' Saqlash'}</button></>}>
        <div className="g2">
          <div className="form-group"><label className="form-label">Icon</label><input className="form-input" value={form.icon} onChange={e => setForm({...form, icon: e.target.value})} placeholder="" style={{ fontSize: 18, textAlign: 'center' }} /></div>
          <div className="form-group"><label className="form-label">Tartib raqami</label><input className="form-input" type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: +e.target.value})} /></div>
        </div>
        <div className="form-group"><label className="form-label">Nomi (O'zbekcha) *</label><input className="form-input" value={form.name_uz} onChange={e => setForm({...form, name_uz: e.target.value})} placeholder="Fishing hujumi" /></div>
        <div className="form-group"><label className="form-label">Nomi (Ruscha)</label><input className="form-input" value={form.name_ru} onChange={e => setForm({...form, name_ru: e.target.value})} placeholder="Фишинг-атака" /></div>
      </Modal>

      <ConfirmModal
        open={!!delCat}
        onClose={() => setDelCat(null)}
        onConfirm={confirmDelete}
        title="Kategoriyani o'chirish"
        text={`«${delCat?.name_uz || ''}» kategoriyasi ro'yxatdan butunlay o'chiriladi. Qaytarib bo'lmaydi.`}
        confirmLabel="O'chirish"
        danger
      />
    </Card>
  )
}

// ═══════════════════════════════════════════════════════
//  TEMPLATES
// ═══════════════════════════════════════════════════════
// Shablon holati bitta qoidadan chiqadi (kit "Shablonlar" bo'limi, 12):
// faol — yashil, AI yaratgan qoralama — sariq (operator e'tibori kerak),
// qo'lda o'chirib qo'yilgani — neytral. Holat faqat rang bilan berilmaydi —
// yonida ikonka va matn turadi.
const shablonHolati = (t) =>
  t.is_active
    ? { label: 'Faol', tone: 'ok', Icon: HiCheck }
    : t.source === 'ai'
      ? { label: 'AI qoralama', tone: 'medium', Icon: HiSparkles }
      : { label: 'Nofaol', tone: 'neutral', Icon: HiPause }

// Bitta yo'riqnoma bir necha turkumga tegishli bo'ladi ("APK o'rnatgandim,
// kartamdan pul yechildi" — ham APK virus, ham Karta va kredit), shuning uchun
// bitta tanlagich o'rniga ko'p tanlovli chiplar (resurslardagi kabi).
const TMPL_EMPTY_FORM = { title: '', category_ids: [], body_uz: '', body_uz_cy: '', body_ru: '' }

/**
 * Shablon tanasi uchta tilda saqlanadi. Ro'yxat ham, tahrir oynasi ham
 * shu BITTA jadvaldan quriladi — ilgari uch ustun qo'lda ko'chirib yozilgan
 * edi va har tuzatish uch joyda takrorlanardi.
 *
 * Bo'sh tarjima — jimgina nuqson: bo'sh maydon o'rniga bot lotinchani
 * yuboradi, ya'ni KIRILLcha yozgan fuqaro LOTINcha javob oladi. Shuning
 * uchun qamrov kartochkada ham ko'rinadi (`Coverage`), filtrda ham
 * ("Tarjimasi yo'q").
 */
const TMPL_TILLAR = [
  {
    key: 'body_uz', short: 'UZ', label: "O'zbekcha (lotin) *",
    placeholder: "{ism}, {ticket_raqami} kabi o'zgaruvchilardan foydalaning",
  },
  {
    key: 'body_uz_cy', short: 'КИР', label: 'Ўзбекча (кирилл)',
    target: 'uz_cy', tarjima: "Lotindan o'girish", TarjimaIcon: HiArrowPath,
    placeholder: "Bo'sh qolsa bot lotinchasini yuboradi",
  },
  {
    key: 'body_ru', short: 'RU', label: 'Русский',
    target: 'ru', tarjima: 'AI tarjima', TarjimaIcon: HiSparkles,
    placeholder: "Bo'sh qolsa bot o'zbekchasini yuboradi",
  },
]

/** Kartochkadagi til qamrovi — kit `Coverage` uchun. */
const tilQamrovi = (t) =>
  TMPL_TILLAR.map(f => ({ key: f.key, label: f.short, filled: Boolean((t[f.key] || '').trim()) }))

const tulaQamrov = (t) => TMPL_TILLAR.every(f => (t[f.key] || '').trim())

// Saralash frontendда: backend tartibi qat'iy `use_count DESC`, 43 shablondan
// 33 tasida esa u NOL — bunday holatda SQL barqaror tartib bermaydi va ro'yxat
// har yangilashda joy almashardi. Shuning uchun har bir solishtirish sarlavha
// bo'yicha ikkinchi tayanchga ega.
const TMPL_SARALASH = [
  { value: 'use_count', label: 'Ishlatilishi' },
  { value: 'created_at', label: 'Sana' },
  { value: 'title', label: 'Alifbo' },
]

/**
 * Shablon kartochkasi — kit "Shablonlar" bo'limidagi namunaning aynan o'zi.
 *
 * Tanani `TgText` chizadi: satr allowlist-parser bilan React tugunlariga
 * aylanadi, `innerHTML` hech qachon chaqirilmaydi. Bu bezak emas — AI
 * qoralamasining tanasi fuqaro Telegramda yozgan matndan quriladi, ya'ni
 * unga ishonib bo'lmaydi. Kesish ham `clamp` bilan VIZUAL: ilgarigi
 * `substring(120)` teglarni ham sanardi, shuning uchun kesish `<b` ning
 * o'rtasiga tushib, har kartada ko'rinadigan matn uzunligi har xil edi.
 *
 * Havola ataylab `links="text"`: kartochka ichidagi bosiladigan havola
 * qo'shni tugmalar bilan yanglishtiriladi.
 */
function ShablonKarta({ t, emojis, canManage, onToggle, onCopy, onEdit, onDelete }) {
  const holat = shablonHolati(t)
  // Nusxa — operatorning kundalik amali, shuning uchun u boshqaruvchida ham
  // ko'rinib turadi (ikkilamchi tugma), oddiy operatorda esa yagona amal.
  const nusxa = {
    label: 'Nusxa', icon: HiClipboardDocumentList, onClick: () => onCopy(t),
    ariaLabel: `${t.title} — matnini nusxalash`,
  }

  return (
    <div className="ui-col ui-gap-8" role="listitem" style={{ padding: 'var(--ui-sp-16)' }}>
      <h3 style={{ margin: 0, fontSize: 'var(--ui-fs-md)', fontWeight: 600 }}>{t.title}</h3>

      <div className="ui-row ui-wrap ui-gap-8">
        <Badge tone={holat.tone} icon={holat.Icon}>{holat.label}</Badge>
        {/* Barcha turkumlar — shablon bir nechtasiga tegishli bo'lishi mumkin;
            eski yozuvlarda faqat legacy nom qoladi */}
        {t.categories?.length
          ? t.categories.map(c => <Badge key={c.id} tone="neutral">{c.name_uz}</Badge>)
          : t.category_name
            ? <Badge tone="neutral">{t.category_name}</Badge>
            : <Badge tone="medium" icon={HiExclamationTriangle}>Turkumsiz</Badge>}
        <Coverage items={tilQamrovi(t)} label={`${t.title} — til qamrovi`} />
      </div>

      <TgText
        value={t.body_uz}
        emojis={emojis}
        clamp={3}
        links="text"
        emptyText="Matn yo'q"
        className="ui-muted"
        style={{ fontSize: 'var(--ui-fs-xs)' }}
      />

      {/* "Ishlatildi: 0 marta" ATAYLAB yo'q: 43 kartadan 33 tasida takrorlanadigan
          nol — shovqin. Nol qiymat raqam emas, YORLIQ bo'lib beriladi. */}
      <div className="ui-row ui-wrap ui-gap-8" style={{ fontSize: 'var(--ui-fs-xs)' }}>
        {t.use_count > 0
          ? <span className="ui-muted ui-nums">Ishlatildi: {t.use_count} marta</span>
          : <Badge tone="neutral">Ishlatilmagan</Badge>}
      </div>

      {/* To'rt tugma qatori o'rniga kit qoidasi: ko'rinadigan ikkitasi,
          qolgani menyuda, o'chirish esa ajratgichdan keyin — tasodifiy
          bosish uchun birlamchi amaldan uzoqda. */}
      <CardActions
        secondary={canManage ? nusxa : undefined}
        primary={canManage
          ? (t.is_active
            ? { label: "O'chirib qo'yish", icon: HiPause, variant: 'secondary', onClick: () => onToggle(t, false), ariaLabel: `${t.title} — o'chirib qo'yish` }
            : { label: 'Faollashtirish', icon: HiCheck, variant: 'primary', onClick: () => onToggle(t, true), ariaLabel: `${t.title} — faollashtirish` })
          : nusxa}
        menu={canManage ? [
          { label: 'Tahrirlash', icon: HiPencil, onClick: () => onEdit(t) },
          { sep: true },
          { label: "O'chirish", icon: HiTrash, danger: true, onClick: () => onDelete(t) },
        ] : []}
        menuLabel={`${t.title} — yana amallar`}
      />
    </div>
  )
}

/** Yuklanish paytidagi panjara — markazdagi spinner maketni sakratardi. */
function ShablonSkeleti() {
  return (
    <div className="ui-lattice ui-lattice--3" aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="ui-col ui-gap-8" style={{ padding: 'var(--ui-sp-16)' }}>
          <Skeleton width="68%" height={16} />
          <Skeleton width="44%" height={14} />
          <Skeleton variant="block" height={52} />
          <Skeleton width="30%" height={14} />
        </div>
      ))}
    </div>
  )
}

export function Templates() {
  const { hasPermission } = useAuthStore()
  const { addToast } = useStore()
  const canManage = hasPermission('templates')
  const [tmpls, setTmpls] = useState([])
  const [cats, setCats] = useState([])
  const [loading, setLoading] = useState(true)       // faqat BIRINCHI yuklash
  const [refreshing, setRefreshing] = useState(false)
  const [loadedAt, setLoadedAt] = useState(null)
  // Ro'yxat asboblari — 43 shablon va 11 turkum ko'z bilan qidirilmasin
  const [q, setQ] = useState('')
  const [cat, setCat] = useState('')
  const [filter, setFilter] = useState('all')    // all | active | ai | unused | gap
  const [sortKey, setSortKey] = useState('use_count')
  const [sortDir, setSortDir] = useState('desc')
  const [modal, setModal] = useState(false)
  const [editT, setEditT] = useState(null)
  const [form, setForm] = useState(TMPL_EMPTY_FORM)
  const [boshlangich, setBoshlangich] = useState(TMPL_EMPTY_FORM)  // saqlanmagan o'zgarishni bilish uchun
  const [yopishSoraldi, setYopishSoraldi] = useState(false)
  const [xatoTasdiq, setXatoTasdiq] = useState(false)              // buzuq teg bilan saqlashga rozilik
  const [saving, setSaving] = useState(false)
  const [translating, setTranslating] = useState(null)   // 'uz_cy' | 'ru' | null
  const [delTmpl, setDelTmpl] = useState(null)   // o'chirish tasdig'i kutayotgan shablon
  // Premium emoji kutubxonasi — botga /emojiid orqali yuborilganlar.
  // Bo'sh bo'lsa maydonlar oddiy matn maydoni bo'lib ishlayveradi.
  const [emojis, setEmojis] = useState([])

  // Birinchi yuklash skeletni ko'rsatadi, keyingilari esa faqat yangilash
  // tugmasini band qiladi — ro'yxat joyida qoladi va maket sakramaydi.
  const birinchi = useRef(true)
  const load = useCallback(async () => {
    if (birinchi.current) setLoading(true)
    else setRefreshing(true)
    try { setTmpls(await templatesApi.list(canManage)); setLoadedAt(Date.now()) }
    catch {}
    finally { birinchi.current = false; setLoading(false); setRefreshing(false) }
  }, [canManage])

  useEffect(() => {
    load()
    categoriesApi.list().then(setCats).catch(() => {})
    emojisApi.list().then(setEmojis).catch(() => {})
  }, [load])

  const openForm = (t) => {
    const f = t
      ? { title: t.title,
          // eski yozuvlarda faqat legacy category_id bo'lishi mumkin — chip
          // baribir yonib tursin (migratsiya oynasida forma to'g'ri ko'rinadi)
          category_ids: t.category_ids?.length ? t.category_ids : (t.category_id ? [t.category_id] : []),
          body_uz: t.body_uz || '', body_uz_cy: t.body_uz_cy || '', body_ru: t.body_ru || '' }
      : { ...TMPL_EMPTY_FORM }
    setEditT(t || null)
    setForm(f)
    setBoshlangich(f)
    setYopishSoraldi(false)
    setXatoTasdiq(false)
    setModal(true)
  }
  const openAdd = () => openForm(null)
  const openEdit = t => openForm(t)

  // Har til alohida tekshiriladi: buzuq teg butun xabarni to'xtatadi, lekin
  // faqat SHU tilda yozgan fuqaro uchun — masalan `body_ru` sinsa, xato faqat
  // ruscha profilida chiqadi va uzoq vaqt sezilmay qoladi.
  const tekshiruv = useMemo(() => {
    const out = {}
    for (const f of TMPL_TILLAR) out[f.key] = validateTgHtml(form[f.key])
    return out
  }, [form])

  // O'zgaruvchilar uchala tilda bir xil bo'lishi kerak: tarjimada {ism}
  // tushib qolsa, fuqaro murojaatida nomi o'rniga bo'shliq ketadi.
  const ozgaruvchiFarqi = useMemo(() => {
    const asos = tekshiruv.body_uz.vars
    const farq = []
    for (const f of TMPL_TILLAR.slice(1)) {
      if (!form[f.key].trim()) continue
      const bor = tekshiruv[f.key].vars
      const yetmagan = asos.filter(v => !bor.includes(v))
      const ortiqcha = bor.filter(v => !asos.includes(v))
      if (yetmagan.length || ortiqcha.length) farq.push({ short: f.short, yetmagan, ortiqcha })
    }
    return farq
  }, [form, tekshiruv])

  const ozgardi = useMemo(
    () => TMPL_TILLAR.some(f => form[f.key] !== boshlangich[f.key])
      || form.title !== boshlangich.title
      || String(form.category_id ?? '') !== String(boshlangich.category_id ?? ''),
    [form, boshlangich],
  )

  // Saqlanmagan o'zgarish qo'riqchisi: ilgari `onClose` hammasini jimgina
  // tashlab yuborardi. Ikkinchi modal ochilmaydi — savol footer'ning o'zida
  // beriladi, ya'ni foydalanuvchi endigina bosgan joyda.
  const closeNow = () => { setYopishSoraldi(false); setModal(false) }
  const tryClose = () => { if (ozgardi) setYopishSoraldi(true); else closeNow() }

  const handleSave = async () => {
    if (!form.title.trim() || !form.body_uz.trim()) return addToast("Sarlavha va lotincha matn kerak", 'critical')
    // Buzuq teg — Telegram 400 `can't parse entities`, ya'ni xabar fuqaroga
    // UMUMAN bormaydi. Birinchi bosishda ogohlantiramiz (bazada allaqachon
    // buzuq shablonlar bor — ularni tahrirlash bloklanmasligi kerak),
    // ikkinchi bosishda saqlaymiz.
    const buzuq = TMPL_TILLAR.filter(f => !tekshiruv[f.key].ok)
    if (buzuq.length && !xatoTasdiq) {
      setXatoTasdiq(true)
      return addToast(`Telegram bu teglarni qabul qilmaydi (${buzuq.map(f => f.short).join(', ')}) — tuzating yoki «Saqlash»ni qayta bosing`, 'critical')
    }
    setSaving(true)
    try {
      const payload = {
        title: form.title, body_uz: form.body_uz, body_uz_cy: form.body_uz_cy, body_ru: form.body_ru,
        // DIQQAT: turkum id lari UUID SATR — songa o'girish (+) NaN qilib,
        // turkumni jimgina o'chirib yuborardi. Server legacy category_id ni
        // ro'yxatning BIRINCHI elementidan o'zi to'ldiradi.
        category_ids: form.category_ids,
      }
      if (editT) await templatesApi.update(editT.id, payload)
      else await templatesApi.create(payload)
      closeNow(); await load()
    } catch (e) { addToast(e.message, 'critical') }
    finally { setSaving(false) }
  }

  // AI tarjima: lotincha matn manba, natija tegishli maydonga tushadi va qo'lda tahrirlanadi
  const handleTranslate = async (target) => {
    if (!form.body_uz.trim()) return addToast("Avval lotincha matnni kiriting", 'critical')
    setTranslating(target)
    try {
      const res = await templatesApi.translate({ text: form.body_uz, target })
      setForm(f => ({ ...f, [target === 'uz_cy' ? 'body_uz_cy' : 'body_ru']: res.text }))
    } catch (e) { addToast(e.message, 'critical') }
    finally { setTranslating(null) }
  }

  // Qoralamani faollashtirish / faolni o'chirib qo'yish — o'chirishdan farqli, qaytariladigan amal
  const setActive = async (t, is_active) => {
    try { await templatesApi.update(t.id, { is_active }); await load() }
    catch (e) { addToast(e.message, 'critical') }
  }

  const confirmDelete = async () => {
    if (!delTmpl) return
    try { await templatesApi.delete(delTmpl.id); await load(); setDelTmpl(null) } catch (e) { addToast(e.message, 'critical') }
  }

  // Ilgari hisoblagich clipboard XATO bersa ham oshardi. Endi u faqat matn
  // haqiqatan operatorga o'tganda oshadi, va karta darhol yangilanadi —
  // "Ishlatilmagan" yorlig'i sahifani qayta yuklamasdan yo'qoladi.
  const belgila = (t) => {
    templatesApi.use(t.id).catch(() => {})
    setTmpls(list => list.map(x => (x.id === t.id ? { ...x, use_count: (x.use_count || 0) + 1 } : x)))
  }

  // Operator javob maydoni oddiy textarea — premium emoji tegini xom ko'rinishда
  // qo'ymaymiz, fallback belgisi bilan qo'yamiz (fuqaro baribir emojini ko'radi).
  const handleCopy = (t) => {
    const text = stripTgEmoji(t.body_uz || '')
    if (window.location.pathname.includes('/ticket') || window.location.href.includes('/ticket')) {
      window.dispatchEvent(new CustomEvent('faq:insert', { detail: { text } }))
      belgila(t)
    } else {
      navigator.clipboard.writeText(text)
        .then(() => { belgila(t); addToast("Nusxa olindi", 'message') })
        .catch(() => addToast("Nusxa olinmadi — matnni qo'lda belgilang", 'critical'))
    }
  }

  // Qidiruv indeksi TEGSIZ matndan quriladi: hech kim "<b>" deb qidirmaydi,
  // lekin "bloklang" deb qidiradi va u <b> ichida bo'lishi mumkin.
  const qidiruvIndeks = useMemo(() => {
    const m = new Map()
    for (const t of tmpls) {
      // Qidiruv barcha turkum nomlari bo'yicha ham ishlasin
      m.set(t.id, [t.title, ...(t.categories || []).map(c => c.name_uz), t.category_name,
                   ...TMPL_TILLAR.map(f => stripTgHtml(t[f.key] || ''))]
        .filter(Boolean).join(' ').toLowerCase())
    }
    return m
  }, [tmpls])

  const sanoq = useMemo(() => ({
    all: tmpls.length,
    active: tmpls.filter(t => t.is_active).length,
    ai: tmpls.filter(t => !t.is_active && t.source === 'ai').length,
    unused: tmpls.filter(t => !t.use_count).length,
    gap: tmpls.filter(t => !tulaQamrov(t)).length,
  }), [tmpls])

  const turkumlar = useMemo(() => ([
    { value: '', label: 'Barcha turkumlar' },
    { value: 'yoq', label: 'Turkumsiz' },
    ...cats.map(c => ({ value: String(c.id), label: c.name_uz })),
  ]), [cats])

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase()
    const rows = tmpls.filter(t => {
      if (filter === 'active' && !t.is_active) return false
      if (filter === 'ai' && !(!t.is_active && t.source === 'ai')) return false
      if (filter === 'unused' && t.use_count) return false
      if (filter === 'gap' && tulaQamrov(t)) return false
      if (cat === 'yoq' ? t.category_id : cat && String(t.category_id ?? '') !== cat) return false
      if (!s) return true
      return (qidiruvIndeks.get(t.id) || '').includes(s)
    })
    const yon = sortDir === 'asc' ? 1 : -1
    rows.sort((a, b) => {
      let r = 0
      if (sortKey === 'title') r = a.title.localeCompare(b.title, 'uz')
      else if (sortKey === 'created_at') r = (Date.parse(a.created_at) || 0) - (Date.parse(b.created_at) || 0)
      else r = (a.use_count || 0) - (b.use_count || 0)
      // Ikkinchi tayanch — usiz teng qiymatli 33 shablon har renderda joy almashardi
      return r !== 0 ? r * yon : a.title.localeCompare(b.title, 'uz')
    })
    return rows
  }, [tmpls, q, cat, filter, sortKey, sortDir, qidiruvIndeks])

  const draftCount = sanoq.ai
  const filtrBor = Boolean(q.trim() || cat || filter !== 'all')
  const filtrTozala = () => { setQ(''); setCat(''); setFilter('all') }

  return (
    <div className="page-enter">
      <PageHeader
        title="Javob shablonlari"
        sub={
          <span className="ui-row ui-wrap ui-gap-8">
            <span className="ui-nums">{tmpls.length} ta shablon</span>
            {draftCount > 0 && <span className="ui-nums">· {draftCount} ta AI qoralama</span>}
            <Freshness at={loadedAt} />
          </span>
        }
        actions={
          <>
            <Button
              variant="ghost" icon={HiArrowPath} iconOnly
              loading={refreshing} onClick={load}
              title="Yangilash" aria-label="Shablonlarni yangilash"
            />
            {/* Ilgari bu tugma `tmpls.length > 0` shartida edi — bo'sh ro'yxatda
                yo'qolib, EmptyState ichidan chiqardi, ya'ni joyini almashtirardi. */}
            {canManage && <Button variant="primary" icon={HiPlus} onClick={openAdd}>Yangi shablon</Button>}
          </>
        }
      />

      {tmpls.length > 0 && (
        <>
          <Toolbar>
            {/* `Select` kitda width:100% — Toolbar da unga aniq kenglik beriladi,
                qolgan joyni `SearchInput` o'zi oladi. */}
            <SearchInput
              value={q} onChange={setQ}
              placeholder="Sarlavha yoki matn bo'yicha"
              aria-label="Shablonlarni qidirish"
            />
            <Select
              aria-label="Turkum bo'yicha filtr"
              style={{ width: 210 }}
              value={cat}
              onChange={e => setCat(e.target.value)}
              options={turkumlar}
            />
            <SortMenu
              options={TMPL_SARALASH}
              value={sortKey} onChange={setSortKey}
              dir={sortDir} onDirChange={setSortDir}
            />
          </Toolbar>

          <div className="ui-row ui-wrap ui-gap-12" style={{ margin: '12px 0 16px' }}>
            {/* Filtr faqat boshqaruvchiga — oddiy operator ro'yxatida qoralama
                ham, nofaol shablon ham bo'lmaydi. Tor ekranda lenta o'zi
                gorizontal skrollanadi (Segmented o'ramdan chiqmaydi). */}
            {canManage && (
              <div style={{ minWidth: 0, maxWidth: '100%', overflowX: 'auto' }}>
                <Segmented
                  aria-label="Shablon filtri"
                  items={[
                    { value: 'all', label: 'Barchasi', count: sanoq.all },
                    { value: 'active', label: 'Faol', count: sanoq.active },
                    { value: 'ai', label: 'AI qoralamalari', count: sanoq.ai },
                    { value: 'unused', label: 'Ishlatilmagan', count: sanoq.unused },
                    { value: 'gap', label: "Tarjimasi yo'q", count: sanoq.gap },
                  ]}
                  value={filter}
                  onChange={setFilter}
                />
              </div>
            )}
            <span className="ui-grow" />
            <span className="ui-muted ui-nums" role="status">
              {shown.length} / {tmpls.length} ko&apos;rsatilyapti
            </span>
          </div>
        </>
      )}

      {loading
        ? <ShablonSkeleti />
        : tmpls.length === 0
          ? <KitEmptyState
              icon={HiDocumentText}
              title="Shablon yo'q"
              text="Javob shablonlari operator vaqtini tejaydi — birinchisini shu yerda yarating."
              action={canManage && <Button variant="primary" icon={HiPlus} onClick={openAdd}>Yangi shablon</Button>}
            />
          : shown.length === 0
            ? <KitEmptyState
                icon={HiDocumentText}
                title="Bu filtrda shablon yo'q"
                text="Qidiruvni tozalang yoki boshqa filtrni tanlang."
                action={filtrBor && <Button variant="secondary" icon={HiXMark} onClick={filtrTozala}>Filtrlarni tozalash</Button>}
              />
            : (
              <div className="ui-lattice ui-lattice--3" role="list">
                {shown.map(t => (
                  <ShablonKarta
                    key={t.id}
                    t={t}
                    emojis={emojis}
                    canManage={canManage}
                    onToggle={setActive}
                    onCopy={handleCopy}
                    onEdit={openEdit}
                    onDelete={setDelTmpl}
                  />
                ))}
              </div>
            )
      }

      {/* Keng modal: uchala til yonma-yon turadi — tarjimani asl matn bilan
          solishtirish uchun skroll kerak emas. Kenglik ekranga qarab o'sadi,
          ustunlar esa .tmpl-langs da qat'iy uchta (1000px dan pastda ustma-ust). */}
      <Modal
        open={modal}
        onClose={tryClose}
        width="min(1500px, 96vw)"
        title={editT ? 'Shablonni tahrirlash' : 'Yangi shablon'}
        footer={yopishSoraldi
          ? <>
              <span className="ui-muted" style={{ marginRight: 'auto' }}>Saqlanmagan o&apos;zgarishlar yo&apos;qoladi</span>
              <Button variant="secondary" onClick={() => setYopishSoraldi(false)}>Tahrirga qaytish</Button>
              <Button variant="danger" onClick={closeNow}>Baribir yopish</Button>
            </>
          : <>
              <Button variant="secondary" onClick={tryClose}>Bekor</Button>
              <Button variant="primary" loading={saving} onClick={handleSave}>Saqlash</Button>
            </>}
      >
        {/* Sarlavha va kategoriya — bitta qatorda, tor oynada bir-birining tagida */}
        <div className="tmpl-head" style={{ marginBottom: 'var(--ui-sp-16)' }}>
          <Field label="Sarlavha *" htmlFor="tmpl-title">
            <Input id="tmpl-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          </Field>
          {/* Turkumlar — bir nechta: AI kandidatlarni ticket turkumi bo'yicha
              shu bog'lanish orqali topadi */}
          <Field label="Turkumlar" hint="bir nechta tanlash mumkin">
            <ChipSelect
              aria-label="Shablon turkumlari"
              items={cats.map(c => ({ value: c.id, label: c.name_uz, icon: c.icon }))}
              value={form.category_ids}
              onChange={ids => setForm({ ...form, category_ids: ids })}
              empty="Kategoriyalar yo'q"
            />
          </Field>
        </div>

        {/* Uch til bir qatorda. Matn maydonlari premium emojini jonli ko'rsatadi —
            bazaga esa <tg-emoji emoji-id="..."> teg bo'lib tushadi (kit:
            EmojiTextarea). Har ustun tagida fuqaro ko'radigan ko'rinish turadi:
            ilgari admin yozgan HTML nima bo'lib chiqishini FAQAT Telegramda
            ko'rardi, xato bo'lsa esa umuman ko'rmasdi. */}
        <div className="tmpl-langs">
          {TMPL_TILLAR.map(f => {
            const v = tekshiruv[f.key]
            return (
              <div key={f.key} className="ui-col ui-gap-8" style={{ minWidth: 0 }}>
                <div className="form-label-row">
                  <span className="ui-field__label" id={`lbl-${f.key}`} style={{ marginBottom: 0 }}>{f.label}</span>
                  {f.target && (
                    <Button
                      size="sm" variant="secondary" icon={f.TarjimaIcon}
                      loading={translating === f.target}
                      disabled={translating !== null}
                      onClick={() => handleTranslate(f.target)}
                    >
                      {f.tarjima}
                    </Button>
                  )}
                </div>

                <EmojiTextarea
                  aria-labelledby={`lbl-${f.key}`}
                  value={form[f.key]}
                  onChange={val => setForm(prev => ({ ...prev, [f.key]: val }))}
                  emojis={emojis}
                  rows={9}
                  invalid={!v.ok}
                  placeholder={f.placeholder}
                />

                {!v.ok && (
                  <Alert tone="warn" title={`${f.short} — Telegram bu xabarni rad etadi`}>
                    <ul style={{ margin: 0, paddingLeft: '1.1em' }}>
                      {v.errors.map(e => (
                        <li key={`${e.code}-${e.tag}`}>
                          {e.message}
                          {e.count > 1 && <span className="ui-muted ui-nums"> ({e.count} joyda)</span>}
                        </li>
                      ))}
                    </ul>
                  </Alert>
                )}

                <div
                  style={{
                    border: '1px solid var(--ui-border)', borderRadius: 'var(--ui-r)',
                    background: 'var(--ui-surface-2)', padding: 'var(--ui-sp-12)',
                    maxHeight: 200, overflowY: 'auto',
                  }}
                >
                  <div className="ui-field__label">Fuqaro ko&apos;radigan ko&apos;rinish</div>
                  <TgText
                    value={form[f.key]}
                    emojis={emojis}
                    emptyText="Matn yo'q"
                    style={{ fontSize: 'var(--ui-fs-sm)' }}
                  />
                </div>
              </div>
            )
          })}
        </div>

        {ozgaruvchiFarqi.length > 0 && (
          <Alert tone="warn" title="Tarjimada o'zgaruvchilar mos kelmayapti" style={{ marginTop: 'var(--ui-sp-16)' }}>
            <ul style={{ margin: 0, paddingLeft: '1.1em' }}>
              {ozgaruvchiFarqi.map(d => (
                <li key={d.short}>
                  {d.short}:
                  {d.yetmagan.length > 0 && <> yetishmaydi — {d.yetmagan.map(v => `{${v}}`).join(', ')}</>}
                  {d.ortiqcha.length > 0 && <> ortiqcha — {d.ortiqcha.map(v => `{${v}}`).join(', ')}</>}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </Modal>

      <ConfirmModal
        open={!!delTmpl}
        onClose={() => setDelTmpl(null)}
        onConfirm={confirmDelete}
        title="Shablonni o'chirish"
        text={`«${delTmpl?.title || ''}» shabloni bazadan BUTUNLAY o'chiriladi — operatorlar ro'yxatidan ham, qoralamalar ichidan ham yo'qoladi. Qaytarib bo'lmaydi.`}
        confirmLabel="O'chirish"
        danger
      />
    </div>
  )
}

// ═══════════════════════════════════════════════════════
//  RESURSLAR (ИЛГАРИГИ FAQ)
// ═══════════════════════════════════════════════════════
// Resurs turi — emoji o'rniga hi2 ikonkasi + kit Badge ohangi (DESIGN.md 4 va 8).
const CONTENT_TYPE = {
  text:      { Icon: HiDocumentText,        label: 'Matn',        tone: 'neutral' },
  video:     { Icon: HiVideoCamera,         label: 'Video',       tone: 'medium'  },
  photo:     { Icon: HiPhoto,               label: 'Foto',        tone: 'neutral' },
  emergency: { Icon: Beacon,                label: 'Shoshilinch', tone: 'critical' },
}

export function FAQ() {
  const { addToast } = useStore()
  const [faqs, setFaqs] = useState([])
  const [cats, setCats] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [editF, setEditF] = useState(null)
  const [autoTagging, setAutoTagging] = useState(false)
  const [form, setForm] = useState({ question_uz: '', answer_uz: '', question_ru: '', answer_ru: '', priority: 1, category_ids: [] })
  const [saving, setSaving] = useState(false)
  const [delFaq, setDelFaq] = useState(null)   // o'chirish tasdig'i kutayotgan resurs

  // Drag-and-Drop state for reordering resources
  const [dragIndex, setDragIndex] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)
  const [isOrderChanged, setIsOrderChanged] = useState(false)
  const [savingOrder, setSavingOrder] = useState(false)
  const [saveSuccessMsg, setSaveSuccessMsg] = useState('')

  // Function to save new priority order to backend
  const updatePriorities = async (newList) => {
    setSavingOrder(true)
    try {
      const payload = newList.map((item, idx) => ({
        id: item.id,
        priority: newList.length - idx
      }))
      const res = await fetch(`${BASE}/faq/reorder`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      })
      if (res.ok) {
        setIsOrderChanged(false)
        setSaveSuccessMsg("Tartib muvaffaqiyatli saqlandi!")
        setTimeout(() => setSaveSuccessMsg(''), 3000)
      }
    } catch (err) {
      console.error("Prioritetlarni saqlashda xato:", err)
      addToast("Tartibni saqlashda xato yuz berdi", 'critical')
    } finally {
      setSavingOrder(false)
    }
  }

  const handleSaveOrder = () => {
    updatePriorities(faqs)
  }

  const handleDragStart = (e, index) => {
    setDragIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', index)
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    if (dragOverIndex !== index) {
      setDragOverIndex(index)
    }

    // Auto-scroll window up or down smoothly when dragging near top/bottom edges
    const topThreshold = 140
    const bottomThreshold = window.innerHeight - 140
    if (e.clientY < topThreshold) {
      window.scrollBy({ top: -20, behavior: 'smooth' })
    } else if (e.clientY > bottomThreshold) {
      window.scrollBy({ top: 20, behavior: 'smooth' })
    }
  }

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault()
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null)
      setDragOverIndex(null)
      return
    }

    const updatedFaqs = [...faqs]
    const [draggedItem] = updatedFaqs.splice(dragIndex, 1)
    updatedFaqs.splice(dropIndex, 0, draggedItem)

    // Dynamic priority calculation (highest priority on top)
    const newFaqs = updatedFaqs.map((item, idx) => ({
      ...item,
      priority: updatedFaqs.length - idx
    }))

    setFaqs(newFaqs)
    setIsOrderChanged(true)
    setDragIndex(null)
    setDragOverIndex(null)

    // Auto save priorities to API
    await updatePriorities(newFaqs)
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${BASE}/faq`, { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } })
      setFaqs(await res.json())
    } catch {}
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load(); categoriesApi.list().then(setCats).catch(() => {}) }, [load])

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access_token')}` }

  const openAdd = () => { setEditF(null); setForm({ question_uz: '', answer_uz: '', question_ru: '', answer_ru: '', priority: 1, category_ids: [], content_type: 'text', keywords: '', target_intent: '', summary: '' }); setModal(true) }
  const openEdit = f => {
    setEditF(f)
    setForm({
      question_uz: f.question_uz || '', answer_uz: f.answer_uz || '',
      question_ru: f.question_ru || '', answer_ru: f.answer_ru || '',
      priority: f.priority ?? 1,
      category_ids: f.category_ids || [],
      content_type: f.content_type || 'text',
      keywords: f.keywords || '',
      target_intent: f.target_intent || '',
      summary: f.summary || '',
    })
    setModal(true)
  }

  const handleAutoTag = async () => {
    if (!form.question_uz && !form.answer_uz) return addToast("Avto-teglash uchun sarlavha yoki matn kiriting", 'critical')
    setAutoTagging(true)
    try {
      const res = await fetch(`${BASE}/faq/auto_tag`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ title: form.question_uz, text: form.answer_uz })
      })
      if (res.ok) {
        const data = await res.json()
        setForm(f => ({
          ...f,
          content_type: data.content_type || f.content_type,
          keywords: data.keywords || f.keywords,
          target_intent: data.target_intent || f.target_intent,
          summary: data.summary || f.summary
        }))
      }
    } catch (e) {
      console.error("Auto tag error:", e)
    } finally {
      setAutoTagging(false)
    }
  }


  const handleSave = async () => {
    if (!form.question_uz || !form.answer_uz) return addToast("Sarlavha va havola kerak", 'critical')
    setSaving(true)
    try {
      // faqat tahrirlanadigan maydonlar (id/view_count yuborilmaydi)
      const payload = {
        question_uz: form.question_uz, answer_uz: form.answer_uz,
        question_ru: form.question_ru, answer_ru: form.answer_ru,
        priority: parseInt(form.priority) || 1,
        category_ids: form.category_ids,
        content_type: form.content_type || 'text',
        keywords: form.keywords || '',
        target_intent: form.target_intent || '',
        summary: form.summary || '',
      }
      const url = editF ? `${BASE}/faq/${editF.id}` : `${BASE}/faq`
      const res = await fetch(url, { method: editF ? 'PUT' : 'POST', headers, body: JSON.stringify(payload) })
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Saqlashda xato') }
      setModal(false); await load()
    } catch (e) { addToast(e.message, 'critical') }
    finally { setSaving(false) }
  }

  const confirmDelete = async () => {
    if (!delFaq) return
    try { await fetch(`${BASE}/faq/${delFaq.id}`, { method: 'DELETE', headers }); await load(); setDelFaq(null) } catch (e) { addToast(e.message, 'critical') }
  }

  return (
    <div className="page-enter">
      <div className="page-hdr">
        <div className="page-hdr-left"><h2>Resurslar — Muhim Postlar va Video Yo'riqnomalar</h2><p>{faqs.length} ta resurs</p></div>
        <div className="page-hdr-right">
          <button className="btn-refresh-icon" onClick={load} title="Yangilash" aria-label="Resurslar ro'yxatini yangilash">
            <HiArrowPath />
          </button>
          {faqs.length > 0 && (
            <button
              className={`btn ${isOrderChanged ? 'btn-success' : 'btn-secondary'}`}
              onClick={handleSaveOrder}
              disabled={savingOrder}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600
              }}
            >
              {savingOrder ? "Saqlanmoqda..." : <><HiArrowsUpDown aria-hidden="true" /> {isOrderChanged ? "Tartibni saqlash (o'zgardi)" : "Tartibni saqlash"}</>}
            </button>
          )}
          {faqs.length > 0 && <button className="btn btn-primary" onClick={openAdd}>+ Resurs qo'shish</button>}
        </div>
      </div>
      {saveSuccessMsg && (
        <Alert tone="ok" icon={HiCheckCircle} style={{ marginBottom: 16 }}>{saveSuccessMsg}</Alert>
      )}
      {loading ? <div style={{ textAlign: 'center', padding: 40 }}><Spinner /></div> : (
        <Card>
          {faqs.length === 0 ? <EmptyState icon="" text="Resurslar yo'q" action={<button className="btn btn-primary" onClick={openAdd}>+ Qo'shish</button>} /> :
           faqs.map((f, i) => {
              const tgLinks = extractTelegramLinks(f.answer_uz)
              const firstTg = tgLinks[0]
              const isDragging = dragIndex === i
              const isDragOver = dragOverIndex === i

              return (
                <div 
                  key={f.id} 
                  draggable
                  onDragStart={(e) => handleDragStart(e, i)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDrop={(e) => handleDrop(e, i)}
                  onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                  style={{ 
                    padding: '12px 16px',
                    borderBottom: i < faqs.length - 1 ? '1px solid var(--ui-border)' : 'none',
                    display: 'flex',
                    gap: 12,
                    cursor: 'default',
                    alignItems: 'center',
                    background: isDragging
                      ? 'var(--ui-surface-2)'
                      : isDragOver
                        ? 'var(--ui-surface-3)'
                        : 'var(--ui-surface)',
                    opacity: isDragging ? 0.45 : 1,
                    borderTop: isDragOver ? '2px solid var(--ui-text-1)' : 'none',
                    boxShadow: isDragging ? 'var(--ui-shadow-pop)' : 'none',
                    transition: 'background var(--ui-dur) var(--ui-ease), opacity var(--ui-dur) var(--ui-ease)'
                  }}
                  onMouseEnter={e => { if (dragIndex === null) e.currentTarget.style.background = 'var(--ui-surface-2)' }}
                  onMouseLeave={e => { if (dragIndex === null) e.currentTarget.style.background = '' }}
                >
                  {/* Drag Handle Icon (6 dots grip-vertical) */}
                  <div
                    style={{
                      cursor: 'grab',
                      color: 'var(--ui-text-3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4px 2px',
                      borderRadius: 'var(--ui-r)',
                      flexShrink: 0
                    }}
                    title="Surish uchun ushlang (Drag to reorder)"
                    onMouseDown={(e) => { e.currentTarget.style.cursor = 'grabbing' }}
                    onMouseUp={(e) => { e.currentTarget.style.cursor = 'grab' }}
                  >
                    <svg width="14" height="18" viewBox="0 0 14 18" fill="currentColor" aria-hidden="true">
                      <circle cx="4" cy="3" r="1.5" />
                      <circle cx="10" cy="3" r="1.5" />
                      <circle cx="4" cy="9" r="1.5" />
                      <circle cx="10" cy="9" r="1.5" />
                      <circle cx="4" cy="15" r="1.5" />
                      <circle cx="10" cy="15" r="1.5" />
                    </svg>
                  </div>

                  {/* 1. Row Number */}
                  <div style={{
                    width: 24,
                    height: 24,
                    borderRadius: 'var(--ui-r)',
                    background: 'var(--ui-surface-2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    color: 'var(--ui-text-1)',
                    flexShrink: 0
                  }}>
                    {i + 1}
                  </div>

                  {/* 2. Media Photo Thumbnail */}
                  {f.image_url ? (
                    <div style={{
                      width: 120,
                      height: 80,
                      flexShrink: 0,
                      borderRadius: 'var(--ui-r-lg)',
                      overflow: 'hidden',
                      border: '1px solid var(--ui-border)',
                      background: 'var(--ui-surface-2)'
                    }}>
                      <img 
                        src={f.image_url} 
                        alt={f.question_uz || ''} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} 
                        onError={(e) => { e.currentTarget.parentElement.style.display = 'none' }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      width: 120,
                      height: 80,
                      flexShrink: 0,
                      borderRadius: 'var(--ui-r-lg)',
                      border: '1px dashed var(--ui-border)',
                      background: 'var(--ui-surface-2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--ui-text-3)'
                    }}>
                      <HiPhoto size={20} aria-hidden="true" />
                      <span className="ui-sr-only">Rasm yo'q</span>
                    </div>
                  )}

                  {/* 3. Middle Column - Title & Metadata */}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ui-text-1)', lineHeight: 1.4 }}>
                      {firstTg ? (
                        <a
                          href={firstTg.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                        >
                          {f.question_uz}
                        </a>
                      ) : (
                        f.question_uz
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ui-text-3)' }}>
                        {f.view_count || 0} marta foydalanildi
                      </span>
                      {(() => {
                        const ct = CONTENT_TYPE[f.content_type] || CONTENT_TYPE.text
                        return <Badge tone={ct.tone} icon={ct.Icon}>{ct.label}</Badge>
                      })()}
                      {(f.categories || []).map(c => (
                        <Badge key={c.id} tone="neutral">{c.icon} {c.name_uz}</Badge>
                      ))}
                    </div>

                    {f.answer_uz && f.answer_uz !== firstTg?.url && (
                      <div style={{ fontSize: 12, color: 'var(--ui-text-3)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        <RichText text={f.answer_uz} />
                      </div>
                    )}
                  </div>

                  {/* 4. Right Column - Priority Tag & Aligned Action Controls (Matching Left Bar Style) */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    {/* Priority Badge - Styled like Left Index Badge */}
                    <div style={{
                      height: 32,
                      padding: '0 8px',
                      borderRadius: 'var(--ui-r)',
                      background: 'var(--ui-surface-2)',
                      color: 'var(--ui-text-2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 500,
                      fontVariantNumeric: 'tabular-nums',
                      border: '1px solid var(--ui-border)'
                    }}>
                      Prioritet: {f.priority || 1}
                    </div>

                    {firstTg && (
                      <a
                        href={firstTg.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          height: 32,
                          padding: '0 12px',
                          borderRadius: 'var(--ui-r)',
                          background: 'var(--ui-surface)',
                          color: 'var(--ui-text-1)',
                          fontSize: 12,
                          fontWeight: 600,
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          border: '1px solid var(--ui-border)',
                          transition: 'background var(--ui-dur) var(--ui-ease), border-color var(--ui-dur) var(--ui-ease)'
                        }}
                      >
                        Postni ochish <HiArrowTopRightOnSquare size={13} aria-hidden="true" />
                      </a>
                    )}

                    {/* Action Buttons Toolbar */}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button
                        className="btn btn-secondary btn-icon"
                        title="Tahrirlash"
                        aria-label={`${f.question_uz || 'Resurs'} — tahrirlash`}
                        onClick={() => openEdit(f)}
                        style={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 'var(--ui-r)',
                          padding: 0
                        }}
                      >
                        <HiPencil size={15} />
                      </button>
                      <button
                        className="btn btn-danger btn-icon"
                        title="O'chirish"
                        aria-label={`${f.question_uz || 'Resurs'} — o'chirish`}
                        onClick={() => setDelFaq(f)}
                        style={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 'var(--ui-r)',
                          padding: 0
                        }}
                      >
                        <HiTrash size={15} />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
        </Card>
      )}
      <Modal open={modal} onClose={() => setModal(false)} title={editF ? 'Resursni tahrirlash' : 'Yangi resurs'}
        footer={<><button className="btn btn-secondary" onClick={() => setModal(false)}>Bekor</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? <Spinner size={14}/> : ' Saqlash'}</button></>}>
        <div className="form-group"><label className="form-label">Resurs sarlavhasi (O'zbekcha) *</label><textarea className="form-input" rows={3} style={{ resize: 'vertical' }} value={form.question_uz} onChange={e => setForm({...form, question_uz: e.target.value})} placeholder="Masalan: Bank karta ma'lumotlarini oshkor qilmaslik bo'yicha yo'riqnoma" /></div>
        <div className="form-group"><label className="form-label">Post yoki Video havolasi (O'zbekcha) *</label><input className="form-input" value={form.answer_uz} onChange={e => setForm({...form, answer_uz: e.target.value})} placeholder="https://t.me/jizzax_kiber/20" /></div>
        <TelegramEmbeds text={form.answer_uz} />
        
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleAutoTag}
          disabled={autoTagging}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12, fontWeight: 600, width: '100%', justifyContent: 'center' }}
        >
          {autoTagging ? <Spinner size={14} /> : <><HiSparkles aria-hidden="true" /> AI bilan avto-teglash</>}
        </button>

        <div className="form-group">
          <label className="form-label">Kontent Turi (Content Type)</label>
          <select className="form-input" value={form.content_type} onChange={e => setForm({...form, content_type: e.target.value})}>
            <option value="text">Matnli Yo'riqnoma / Пост</option>
            <option value="video">Video-darslik / Видео</option>
            <option value="photo">Foto / Skrinshot</option>
            <option value="emergency">Shoshilinch Огоҳлантириш</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Kalit so'zlar va Sinonymlar (Keywords) <span style={{ color:'var(--ui-text-3)', fontWeight:400 }}>(vergul bilan)</span></label>
          <input className="form-input" value={form.keywords} onChange={e => setForm({...form, keywords: e.target.value})} placeholder="telegram, 2fa, parol, maxfiy kod" />
        </div>

        <div className="form-group">
          <label className="form-label">Prioritet (1-10) <span style={{ color:'var(--ui-text-3)', fontWeight:400 }}>(Qanchalik yuqori bo'lsa, o'xshash holatlarda birinchi tanlanadi)</span></label>
          <input type="number" className="form-input" min={1} max={10} value={form.priority} onChange={e => setForm({...form, priority: parseInt(e.target.value) || 1})} placeholder="10 = Eng yuqori prioritet" />
        </div>

        {/* Kategoriyalar (task 6) — bir nechta tanlash mumkin.
            Ilgari bu yerda joyida yozilgan chip to'ri turardi; endi kitdagi
            ChipSelect (shablon formasi ham xuddi shuni ishlatadi). */}
        <div className="form-group">
          <label className="form-label">Kategoriyalar <span style={{ color:'var(--ui-text-3)', fontWeight:400 }}>(bir nechta tanlash mumkin)</span></label>
          <ChipSelect
            aria-label="Resurs kategoriyalari"
            items={cats.map(c => ({ value: c.id, label: c.name_uz, icon: c.icon }))}
            value={form.category_ids}
            onChange={ids => setForm({ ...form, category_ids: ids })}
            empty="Kategoriyalar yo'q"
          />
        </div>
        <div className="form-group"><label className="form-label">Sarlavha (Ruscha)</label><textarea className="form-input" rows={2} style={{ resize: 'vertical' }} value={form.question_ru} onChange={e => setForm({...form, question_ru: e.target.value})} /></div>
        <div className="form-group"><label className="form-label">Havola (Ruscha)</label><input className="form-input" value={form.answer_ru} onChange={e => setForm({...form, answer_ru: e.target.value})} placeholder="https://t.me/jizzax_kiber/20" /></div>
      </Modal>

      <ConfirmModal
        open={!!delFaq}
        onClose={() => setDelFaq(null)}
        onConfirm={confirmDelete}
        title="Resursni o'chirish"
        text={`«${delFaq?.question_uz || ''}» resursi o'chiriladi. Bot va AI javoblarida bu resurs boshqa ishlatilmaydi.`}
        confirmLabel="O'chirish"
        danger
      />
    </div>
  )
}

// ═══════════════════════════════════════════════════════
//  BACKUPS CARD
// ═══════════════════════════════════════════════════════
export function BackupsCard() {
  const { addToast } = useStore()
  const [backups, setBackups] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [restoring, setRestoring] = useState(null)
  const [modalRestore, setModalRestore] = useState(null)
  const [delBackup, setDelBackup] = useState(null)   // o'chirish tasdig'i kutayotgan nusxa fayli

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setBackups(await backupsApi.list())
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleCreate = async () => {
    setCreating(true)
    try {
      const res = await backupsApi.create()
      await load()
      // Offsite (R2) natijasini ko'rsatamiz — aks holda R2 jimgina bo'sh qoladi
      const off = res?.offsite
      if (off?.ok) {
        addToast("Zaxira yaratildi va Cloudflare R2 ga yuklandi", 'message')
      } else if (off?.skipped) {
        addToast("Zaxira yaratildi, ammo R2 (offsite) ga yuklanmadi: R2 o'chirilgan yoki «Avtomatik yuklash» yoqilmagan. Sozlamalar → «Offsite zahira — Cloudflare R2».", 'critical')
      } else if (off && off.ok === false) {
        addToast("Zaxira yaratildi, ammo R2 ga yuklashda xato: " + (off.error || "noma'lum"), 'critical')
      }
    } catch (e) {
      addToast("Nusxa yaratishda xato: " + e.message, 'critical')
    } finally {
      setCreating(false)
    }
  }

  const handleRestore = async (fname) => {
    setRestoring(fname)
    try {
      await backupsApi.restore(fname)
      addToast("Baza muvaffaqiyatli tiklandi", 'message')
      setModalRestore(null)
      window.location.reload()
    } catch (e) {
      addToast("Tiklashda xato: " + e.message, 'critical')
    } finally {
      setRestoring(null)
    }
  }

  const confirmDelete = async () => {
    if (!delBackup) return
    try {
      await backupsApi.remove(delBackup)
      await load()
      setDelBackup(null)
    } catch (e) {
      addToast("O'chirishda xato: " + e.message, 'critical')
    }
  }

  const totalSizeMb = backups.reduce((acc, b) => acc + (b.size_mb || 0), 0).toFixed(2)

  return (
    <Card 
      title="Резервные копии (Razerat ko'chirmalari)" 
      subtitle={`Jami: ${backups.length} ta nusxa (${totalSizeMb} MB)`}
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-refresh-icon" onClick={load} title="Yangilash" aria-label="Zaxira nusxalari ro'yxatini yangilash">
            <HiArrowPath />
          </button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
            {creating ? <Spinner size={14} /> : '+ Zudlik bilan nusxa yaratish'}
          </button>
        </div>
      }
    >
      <div className="card-body" style={{ paddingTop: 0 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 30 }}><Spinner /></div>
        ) : backups.length === 0 ? (
          <EmptyState icon="" text="Hali rezerv nusxalar yaratilmagan" action={<button className="btn btn-primary" onClick={handleCreate}>+ Yaratish</button>} />
        ) : (
          <table className="table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--ui-border)', textAlign: 'left', color: 'var(--ui-text-2)', fontSize: 12 }}>
                <th style={{ padding: '10px 12px' }}>Fayl nomi</th>
                <th style={{ padding: '10px 12px' }}>Yaratilgan vaqti</th>
                <th style={{ padding: '10px 12px' }}>Hajmi</th>
                <th style={{ padding: '10px 12px' }}>Turi</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {backups.map(b => (
                <tr key={b.filename} style={{ borderBottom: '1px solid var(--ui-border)' }}>
                  <td style={{ padding: '12px', fontWeight: 600, color: 'var(--ui-text-1)' }}>{b.filename}</td>
                  <td style={{ padding: '12px', color: 'var(--ui-text-2)' }}>{new Date(b.created_at).toLocaleString()}</td>
                  <td style={{ padding: '12px', color: 'var(--ui-text-2)' }}>{b.size_mb} MB</td>
                  <td style={{ padding: '12px' }}>
                    <Badge tone={b.tag === 'safety' ? 'medium' : 'neutral'}>
                      {b.tag === 'manual' ? 'Qo\'lda' : (b.tag === 'safety' ? 'Страховочная' : 'Avto')}
                    </Badge>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      <a
                        href={backupsApi.downloadUrl(b.filename)}
                        download
                        className="btn btn-secondary btn-icon"
                        title="Yuklab olish"
                        aria-label={`${b.filename} — yuklab olish`}
                        style={{ width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--ui-r)', padding: 0 }}
                      >
                        <HiArrowDownTray size={14} />
                      </a>
                      <button
                        className="btn btn-secondary btn-icon"
                        title="Tiklash"
                        aria-label={`${b.filename} — bazani tiklash`}
                        onClick={() => setModalRestore(b.filename)}
                        style={{ width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--ui-r)', padding: 0, color: 'var(--ui-st-medium)' }}
                      >
                        <HiArrowPathRoundedSquare size={14} />
                      </button>
                      <button
                        className="btn btn-danger btn-icon"
                        title="O'chirish"
                        aria-label={`${b.filename} — o'chirish`}
                        onClick={() => setDelBackup(b.filename)}
                        style={{ width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--ui-r)', padding: 0 }}
                      >
                        <HiTrash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalRestore && (
        <Modal
          open={!!modalRestore}
          onClose={() => setModalRestore(null)}
          title="Bazani tiklashni tasdiqlang"
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setModalRestore(null)}>Bekor qilish</button>
              <button className="btn btn-primary" onClick={() => handleRestore(modalRestore)} disabled={restoring}>
                {restoring ? <Spinner size={14} /> : "Tiklashni boshlash"}
              </button>
            </>
          }
        >
          <div style={{ fontSize: 13, color: 'var(--ui-text-1)', lineHeight: 1.6 }}>
            <Alert tone="danger" title="DIQQAT!" style={{ marginBottom: 12 }}>
              Bu amalni ortga qaytarib bo'lmaydi.
            </Alert>
            <p>Siz bazani <b>{modalRestore}</b> nusxasidan tiklamoqchisiz.</p>
            <p>Joriy ma'lumotlar ushbu nusxa holatiga qaytariladi. Tiklashdan oldin tizim avtomatik ravishda страховочный nusxa yaratadi.</p>
          </div>
        </Modal>
      )}

      <ConfirmModal
        open={!!delBackup}
        onClose={() => setDelBackup(null)}
        onConfirm={confirmDelete}
        title="Zaxira nusxasini o'chirish"
        text={`${delBackup || ''} fayli serverdan o'chiriladi. Ushbu nusxadan keyin bazani tiklab bo'lmaydi.`}
        confirmLabel="O'chirish"
        danger
      />
    </Card>
  )
}

// ═══════════════════════════════════════════════════════
//  SETTINGS
// ═══════════════════════════════════════════════════════
export function Settings() {
  const { sidebarCollapsed } = useStore()
  const [s, setS] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [activeTab, setActiveTab] = useState(() => {
    const hash = window.location.hash.replace('#', '')
    return hash || 'bots'
  })

  useEffect(() => {
    window.location.hash = activeTab
  }, [activeTab])

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try { setS(await settingsApi.get()) }
    catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  const upd = (key, val) => setS(prev => ({ ...prev, [key]: val }))

  const handleSave = async () => {
    setSaving(true); setSuccess(false); setError(null)
    try {
      await settingsApi.update(s)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (e) { setError(e.message) }
    finally { setSaving(false) }
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spinner size={32} /></div>

  const sections = [
    { id: 'bots',       label: 'Telegram sozlamalari', icon: HiPaperAirplane },
    { id: 'categories', label: 'Kategoriyalar',         icon: HiTag },
    { id: 'audit',      label: 'Audit Jurnali',          icon: HiClipboardDocumentList },
    { id: 'ai',         label: 'AI Operator',           icon: HiSparkles },
    { id: 'vt',         label: 'VirusTotal',            icon: HiShieldCheck },
    { id: 'time',       label: 'Ish vaqti',             icon: HiClock },
    { id: 'notif',      label: 'Bildirishnomalar',      icon: HiBell },
    { id: 'sec',        label: 'Xavfsizlik',            icon: HiLockClosed },
    { id: 'bank',       label: 'Bank Raqamlari',        icon: HiCreditCard },
    { id: 'alarm',      label: 'Trevoga (SOS)',         icon: Beacon },
    { id: 'backups',    label: 'Zahira nusxalari',      icon: HiCircleStack }
  ]

  return (
    /* Yon tomonlarda -28px edi: sahifa to'ldirishi (24px) dan 4px ko'p, ya'ni
       mazmun sahifadan chiqib turar va keraksiz gorizontal sursurgich chiqarardi. */
    <div className="page-enter" style={{ margin: '-24px', minHeight: 'calc(100vh - 20px)', display: 'flex' }}>
      {/* Main Content Area */}
      <div style={{ flex: 1, padding: '24px 32px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 24, marginLeft: 240, marginRight: 0 }}>
        <div className="page-hdr" style={{ marginBottom: 0 }}>
          <div className="page-hdr-left"><h2>Tizim Sozlamalari</h2><p>Asosiy konfiguratsiya va xavfsizlik</p></div>
          <div className="page-hdr-right">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size={14} /> : 'Saqlash'}
            </button>
          </div>
        </div>

        {error && <Alert tone="danger">{error}</Alert>}
        {success && <Alert tone="ok" icon={HiCheckCircle}>Sozlamalar saqlandi</Alert>}

        {s && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 40 }}>
            
            {(activeTab === 'bots' || activeTab === 'userbots') && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <Bots />
                <UserBots />
              </div>
            )}

            {activeTab === 'categories' && <Categories />}

            {activeTab === 'audit' && <Audit />}

            {activeTab === 'ai' && <AIOperatorCard />}

            {activeTab === 'vt' && <VTSettingsCard />}

            {activeTab === 'backups' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <BackupsCard />
                <R2SettingsCard />
              </div>
            )}

            {activeTab === 'time' && (
              <Card title="Ish vaqti">
            <div className="card-body">
              <SettingsRow label="Ish vaqti cheklovi" sub="Yoqilganda bot ish vaqtidan tashqari yangi murojaat qabul qilmaydi">
                <Toggle on={!!s.work_hours_enabled} onChange={v => upd('work_hours_enabled', v)} />
              </SettingsRow>
              <SettingsRow label="Boshlanishi" sub="Ertalab (Toshkent vaqti)">
                <input type="time" className="input" style={{ width: 110 }}
                  value={`${String(s.work_start ?? 9).padStart(2, '0')}:${String(s.work_start_min ?? 0).padStart(2, '0')}`}
                  onChange={e => { const [h, m] = e.target.value.split(':').map(Number); setS(p => ({ ...p, work_start: h, work_start_min: m })) }} />
              </SettingsRow>
              <SettingsRow label="Tugashi" sub="Kechqurun (Toshkent vaqti)">
                <input type="time" className="input" style={{ width: 110 }}
                  value={`${String(s.work_end ?? 18).padStart(2, '0')}:${String(s.work_end_min ?? 0).padStart(2, '0')}`}
                  onChange={e => { const [h, m] = e.target.value.split(':').map(Number); setS(p => ({ ...p, work_end: h, work_end_min: m })) }} />
              </SettingsRow>
              {(() => {
                const days = (s.work_days || '').split(',').filter(Boolean)
                const setDay = (d, on) => {
                  const nd = on ? [...new Set([...days, d])] : days.filter(x => x !== d)
                  upd('work_days', nd.sort().join(','))
                }
                const DAYS = [
                  { id: '1', name: 'Dushanba', abbr: 'Du' },
                  { id: '2', name: 'Seshanba', abbr: 'Se' },
                  { id: '3', name: 'Chorshanba', abbr: 'Ch' },
                  { id: '4', name: 'Payshanba', abbr: 'Pa' },
                  { id: '5', name: 'Juma', abbr: 'Ju' },
                  { id: '6', name: 'Shanba', abbr: 'Sh' },
                  { id: '7', name: 'Yakshanba', abbr: 'Ya' },
                ]
                return (
                  <SettingsRow label="Ish kunlari" sub="Tanlangan kunlar faol hisoblanadi">
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      {DAYS.map(day => {
                        const active = days.includes(day.id)
                        return (
                          <button
                            key={day.id}
                            type="button"
                            title={day.name}
                            aria-label={day.name}
                            aria-pressed={active}
                            onClick={() => setDay(day.id, !active)}
                            style={{
                              width: 38,
                              height: 38,
                              borderRadius: '50%',
                              border: active ? '1px solid var(--ui-text-1)' : '1px solid var(--ui-border)',
                              background: active ? 'var(--ui-text-1)' : 'var(--ui-surface-2)',
                              color: active ? 'var(--ui-surface)' : 'var(--ui-text-2)',
                              fontSize: 12,
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'background var(--ui-dur) var(--ui-ease), border-color var(--ui-dur) var(--ui-ease)',
                              fontFamily: 'inherit',
                            }}
                            onMouseEnter={e => {
                              if (!active) {
                                e.currentTarget.style.background = 'var(--ui-surface-3)'
                                e.currentTarget.style.borderColor = 'var(--ui-border-2)'
                              }
                            }}
                            onMouseLeave={e => {
                              if (!active) {
                                e.currentTarget.style.background = 'var(--ui-surface-2)'
                                e.currentTarget.style.borderColor = 'var(--ui-border)'
                              }
                            }}
                          >
                            {day.abbr}
                          </button>
                        )
                      })}
                    </div>
                  </SettingsRow>
                )
              })()}
            </div>
              </Card>
            )}

            {activeTab === 'notif' && (
              <Card title="Bildirishnomalar va Avto-Javoblar">
            <div className="card-body">
              <SettingsRow label="Kritik murojaat — Telegram" sub="Darhol xabar"><Toggle on={s.notify_critical_tg} onChange={v => upd('notify_critical_tg', v)} /></SettingsRow>
              <SettingsRow label="Kunlik hisobot — Email" sub="Har kuni 09:00"><Toggle on={s.notify_daily_report_email} onChange={v => upd('notify_daily_report_email', v)} /></SettingsRow>
              <SettingsRow label="Botda lokatsiya so'rash" sub="O'chirilsa bot aniq joylashuvni so'ramaydi"><Toggle on={s.location_request_enabled !== false} onChange={v => upd('location_request_enabled', v)} /></SettingsRow>
              <SettingsRow label="Userbot Avto-Yo'naltirish javobi" sub="O'chirilsa shaxsiy akkauntga yozgan fuqarolarga avto-javob yuborilmaydi"><Toggle on={s.userbot_redirect_enabled !== false} onChange={v => upd('userbot_redirect_enabled', v)} /></SettingsRow>
              {s.userbot_redirect_enabled !== false && (
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--ui-border)' }}>
                  <label className="form-label" style={{ fontWeight: 700, display: 'block', marginBottom: 6 }}>
                    Telegram Akkaunt (Userbot) Avto-Yo'naltirish Matni
                  </label>
                  <p style={{ fontSize: 12, color: 'var(--ui-text-3)', margin: '0 0 10px' }}>
                    Shaxsiy Telegram akkauntiga fuqaro yozganda avtomatik yuboriladigan matn. Bot havolasini qo'yish uchun <code>&#123;link&#125;</code> deb yozing.
                  </p>
                  <textarea
                    className="form-input"
                    rows={5}
                    value={s.userbot_redirect_text ?? "Assalomu alaykum! 👋\n\n🔐 RAQAMLI XAVFSIZLIK MARKAZI\n\nMurojaatlar rasmiy botimiz orqali qabul qilinadi. Quyidagi havolani bosib, savolingizni yozing:\n\n👉 {link}"}
                    onChange={e => upd('userbot_redirect_text', e.target.value)}
                    style={{ fontFamily: 'inherit', fontSize: 13, lineHeight: 1.5, width: '100%' }}
                  />
                </div>
              )}
            </div>
              </Card>
            )}


            {activeTab === 'sec' && (
              <Card title="Xavfsizlik">
            <div className="card-body">
              <SettingsRow label="Avtomatik taqsimlash" sub="Round-robin algoritmi"><Toggle on={s.auto_assign} onChange={v => upd('auto_assign', v)} /></SettingsRow>
              <SettingsRow label="Max login urinish" sub="Bloklashdan oldin">
                <input className="form-input" type="number" min={3} max={10} value={s.max_login_attempts || 5} onChange={e => upd('max_login_attempts', +e.target.value)} style={{ width: 70, textAlign: 'center' }} />
              </SettingsRow>
              <SettingsRow label="Bloklash muddati (daqiqa)" sub="">
                <input className="form-input" type="number" min={5} max={60} value={s.lockout_minutes || 15} onChange={e => upd('lockout_minutes', +e.target.value)} style={{ width: 70, textAlign: 'center' }} />
              </SettingsRow>
            </div>
              </Card>
            )}

            {activeTab === 'bank' && (
              <BankContactsEditor s={s} upd={upd} />
            )}

            {activeTab === 'alarm' && (
              <AlarmSettingsEditor s={s} upd={upd} />
            )}
          </div>
        )}
      </div>

      {/* Sub-Navigation Sidebar (Portal to document.body: Pixel-Perfect Header & Aligned Layout) */}
      {typeof document !== 'undefined' && createPortal(
        <div style={{
          position: 'fixed',
          top: 0,
          left: sidebarCollapsed ? 'var(--sb-collapsed, 68px)' : 'var(--sb-width, 240px)',
          bottom: 0,
          width: 240,
          height: '100vh',
          background: 'var(--ui-surface)',
          borderRight: '1px solid var(--ui-border)',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 500,
          transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          {/* Header matching main sidebar logo height */}
          <div style={{
            height: 'var(--topbar-h, 64px)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 24px',
            borderBottom: '1px solid var(--ui-border)',
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--ui-text-3)',
            textTransform: 'uppercase',
            letterSpacing: '.06em',
            flexShrink: 0
          }}>
            Bo'limlar
          </div>

          {/* Navigation Items List */}
          <div style={{
            flex: 1,
            padding: '12px 10px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 4
          }}>
            {sections.map(sec => {
              const isActive = activeTab === sec.id || (sec.id === 'bots' && activeTab === 'userbots');
              const Icon = sec.icon
              return (
                <button
                  key={sec.id}
                  onClick={() => setActiveTab(sec.id)}
                  aria-current={isActive ? 'page' : undefined}
                  style={{
                    background: isActive ? 'var(--ui-surface-2)' : 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    padding: '8px 12px',
                    borderRadius: 'var(--ui-r)',
                    fontSize: 13,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? 'var(--ui-text-1)' : 'var(--ui-text-2)',
                    cursor: 'pointer',
                    transition: 'background var(--ui-dur) var(--ui-ease), color var(--ui-dur) var(--ui-ease)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    position: 'relative',
                    fontFamily: 'inherit'
                  }}
                  onMouseEnter={e => { if(!isActive) { e.currentTarget.style.background = 'var(--ui-surface-2)'; e.currentTarget.style.color = 'var(--ui-text-1)'; } }}
                  onMouseLeave={e => { if(!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--ui-text-2)'; } }}
                >
                  {isActive && (
                    <div aria-hidden="true" style={{
                      position: 'absolute',
                      left: 0,
                      top: 6,
                      bottom: 6,
                      width: 2,
                      background: 'var(--ui-text-1)'
                    }} />
                  )}
                  {Icon && <Icon size={18} aria-hidden="true" style={{ color: isActive ? 'var(--ui-text-1)' : 'var(--ui-text-3)', flexShrink: 0 }} />}
                  <span>{sec.label}</span>
                </button>
              )
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════
//  TREVOGA (SOS) — AI baholaydi, navbatchiga xabar ketadi
// ═══════════════════════════════════════════════════════
function AlarmSettingsEditor({ s, upd }) {
  const [duties, setDuties] = useState(() => s.duty_contacts || [])

  const save = (list) => { setDuties(list); upd('duty_contacts', list) }
  const addDuty = () => save([...duties, { name: '', tg_chat_id: '', is_active: true }])
  const updDuty = (i, field, value) => {
    const list = duties.map((d, idx) => idx === i ? { ...d, [field]: value } : d)
    save(list)
  }
  const removeDuty = (i) => save(duties.filter((_, idx) => idx !== i))

  const th = s.alarm_threshold ?? 7
  const active = duties.filter(d => d.is_active !== false && (d.tg_chat_id || '').trim()).length

  return (
    <Card title="Trevoga (SOS)">
      <div className="card-body">
        <div style={{ fontSize: 13, color: 'var(--ui-text-2)', marginBottom: 16, lineHeight: 1.6 }}>
          AI fuqaroning har bir xabarini 0 dan 10 gacha baholaydi. Ball chegaradan
          oshsa: murojaat kritik bo'ladi, fuqarodan telefon raqami so'raladi va
          navbatchilarga Telegram orqali darhol xabar ketadi.
        </div>

        <SettingsRow label="Trevoga rejimi" sub="O'chiq bo'lsa AI baholamaydi — faqat kalit so'zlar ishlaydi (avvalgidek)">
          <Toggle on={!!s.alarm_enabled} onChange={v => upd('alarm_enabled', v)} />
        </SettingsRow>

        <SettingsRow
          label={`Trevoga chegarasi — ${th}/10`}
          sub="Shu balldan boshlab trevoga ko'tariladi. 8 = faqat eng jiddiy holatlar (pul yo'qolgan, tahdid). 5 = shubhali holatlarda ham. Tavsiya: 7">
          <input className="form-input" type="number" min={1} max={10}
                 value={th}
                 onChange={e => upd('alarm_threshold', Math.max(1, Math.min(10, +e.target.value || 7)))}
                 style={{ width: 70, textAlign: 'center' }} />
        </SettingsRow>

        <div style={{ marginTop: 24, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ui-text-1)' }}>Navbatchilar</div>
            <div style={{ fontSize: 12, color: 'var(--ui-text-3)', marginTop: 2 }}>
              {active > 0
                ? `${active} ta faol — trevoga shularga yuboriladi`
                : "Hech kim qo'shilmagan: trevoga ko'tarilsa xabar YUBORILMAYDI"}
            </div>
          </div>
          <button className="btn btn-primary" onClick={addDuty}>+ Navbatchi qo'shish</button>
        </div>

        {duties.length === 0 ? (
          <div style={{ padding: '24px 16px', textAlign: 'center', border: '1px dashed var(--ui-border)', borderRadius: 'var(--ui-r-lg)', fontSize: 13, color: 'var(--ui-text-3)' }}>
            Ro'yxat bo'sh. Kamida bitta navbatchi qo'shing, aks holda trevoga hech kimga bormaydi.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {duties.map((d, i) => (
              <div key={i} style={{ border: '1px solid var(--ui-border)', borderRadius: 'var(--ui-r-lg)', padding: 16, background: 'var(--ui-surface-2)', position: 'relative' }}>
                <button
                  onClick={() => removeDuty(i)}
                  aria-label={`${d.name || 'Navbatchi'} — o'chirish`}
                  style={{ position: 'absolute', top: 8, right: 8, background: 'var(--ui-st-critical-bg)', color: 'var(--ui-st-critical)', border: '1px solid var(--ui-st-critical)', borderRadius: 'var(--ui-r)', padding: '4px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 500, fontFamily: 'inherit' }}>
                  O'chirish
                </button>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                  <div>
                    <label style={{ fontSize: 11, color: 'var(--ui-text-3)', marginBottom: 2, display: 'block', fontWeight: 500 }}>Ism</label>
                    <input className="form-input" style={{ fontSize: 12, padding: '6px 10px' }}
                           value={d.name || ''} onChange={e => updDuty(i, 'name', e.target.value)}
                           placeholder="Alisher Karimov" />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: 'var(--ui-text-3)', marginBottom: 2, display: 'block', fontWeight: 500 }}>Telegram chat ID</label>
                    <input className="form-input" style={{ fontSize: 12, padding: '6px 10px', fontFamily: 'var(--ui-mono)' }}
                           value={d.tg_chat_id || ''} onChange={e => updDuty(i, 'tg_chat_id', e.target.value.trim())}
                           placeholder="123456789" />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ui-text-2)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={d.is_active !== false}
                           onChange={e => updDuty(i, 'is_active', e.target.checked)} />
                    Faol (trevoga shu odamga yuboriladi)
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 16, padding: 12, background: 'var(--ui-surface-2)', border: '1px solid var(--ui-border)', borderRadius: 'var(--ui-r)', fontSize: 12, color: 'var(--ui-text-2)', lineHeight: 1.6 }}>
          <b>Chat ID ni qanday bilish mumkin?</b> Navbatchi botimizga <b>/start</b> yozishi
          shart — aks holda Telegram xabar yuborishga ruxsat bermaydi. So'ng uning
          chat ID sini <b>@userinfobot</b> ga yozib bilib olish mumkin.
        </div>
      </div>
    </Card>
  )
}

function BankContactsEditor({ s, upd }) {
  const [banks, setBanks] = useState(() => s.bank_contacts || [])

  const addBank = () => {
    const newBanks = [...banks, { bank_nomi: "", aloqa_markazi_raqami: "", qoshimcha_raqam: "" }]
    setBanks(newBanks)
    upd('bank_contacts', newBanks)
  }

  const updateBank = (index, field, value) => {
    const newBanks = [...banks]
    newBanks[index][field] = value
    setBanks(newBanks)
    upd('bank_contacts', newBanks)
  }

  const removeBank = (index) => {
    const newBanks = banks.filter((_, i) => i !== index)
    setBanks(newBanks)
    upd('bank_contacts', newBanks)
  }

  return (
    <Card title="Bank Raqamlari (AI uchun)">
      <div className="card-body">
        <div style={{ fontSize: 13, color: 'var(--ui-text-2)', marginBottom: 16 }}>
          Ushbu ma'lumotlar avtomatik AI operatorga banklar haqidagi savollarda aniq javob berish uchun taqdim etiladi. Quyidagi kartalarni tahrirlashingiz mumkin.
        </div>
        
        {/* Kartochkalar sahifa bilan birga suriladi (ichki sursurgich yo'q), ustun
            soni esa kenglikka moslashadi — `repeat(4, 1fr)` tor ekranda gorizontal
            sursurgich chiqarardi. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {banks.map((bank, index) => (
            <div key={index} style={{ border: '1px solid var(--ui-border)', borderRadius: 'var(--ui-r-lg)', padding: 16, background: 'var(--ui-surface-2)', position: 'relative', display: 'flex', flexDirection: 'column' }}>
              <button
                onClick={() => removeBank(index)}
                aria-label={`${bank.bank_nomi || 'Bank'} — o'chirish`}
                style={{ position: 'absolute', top: 8, right: 8, background: 'var(--ui-st-critical-bg)', color: 'var(--ui-st-critical)', border: '1px solid var(--ui-st-critical)', borderRadius: 'var(--ui-r)', padding: '4px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 500, fontFamily: 'inherit' }}>
                O'chirish
              </button>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16, flex: 1 }}>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--ui-text-3)', marginBottom: 2, display: 'block', fontWeight: 500 }}>Bank nomi</label>
                  <input className="form-input" style={{ fontSize: 12, padding: '6px 10px' }} value={bank.bank_nomi} onChange={e => updateBank(index, 'bank_nomi', e.target.value)} placeholder="Agrobank" />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--ui-text-3)', marginBottom: 2, display: 'block', fontWeight: 500 }}>Aloqa markaz</label>
                  <input className="form-input" style={{ fontSize: 12, padding: '6px 10px' }} value={bank.aloqa_markazi_raqami} onChange={e => updateBank(index, 'aloqa_markazi_raqami', e.target.value)} placeholder="1216" />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: 'var(--ui-text-3)', marginBottom: 2, display: 'block', fontWeight: 500 }}>Qo'shimcha raqam</label>
                  <input className="form-input" style={{ fontSize: 12, padding: '6px 10px' }} value={bank.qoshimcha_raqam} onChange={e => updateBank(index, 'qoshimcha_raqam', e.target.value)} placeholder="+998 71..." />
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <button onClick={addBank} style={{ marginTop: 16, width: '100%', padding: '12px', background: 'var(--ui-surface-2)', color: 'var(--ui-text-1)', border: '1px dashed var(--ui-border-2)', borderRadius: 'var(--ui-r)', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', transition: 'background var(--ui-dur) var(--ui-ease)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--ui-surface-3)'} onMouseLeave={e => e.currentTarget.style.background = 'var(--ui-surface-2)'}>
          + Yangi bank qo'shish
        </button>
      </div>
    </Card>
  )
}


